/**
 * @file useGameSlashCommands.ts
 * @description Multi-stage slash command state machine for in-game actions.
 *
 * Manages the flow: command_select → option_select → target_select → done.
 * Each stage populates the same SlashCommandMenu with contextual options.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { SlashCommandDefinition } from '@/components/panels/chat-commands';
import { fuzzyFilterCommands } from '@/components/panels/chat-commands';
import type { Intent, IntentAttack } from '@danda/shared';
import type {
  DamageType,
  SpellEffect,
  SpellTargetAudience,
} from '@danda/shared/elasticsearch/combat-mechanics';
import { PICKUP_ALL_SENTINEL } from '@danda/shared';
import {
  buildIntent,
  buildToggleAbilityIntent,
  buildModeAbilityIntent,
  buildSelfUseAbilityIntent,
  buildSelfUseItemIntent,
  buildCommunicationIntent,
  buildGiveCoinsIntent,
  parseCoinAmount,
  substitutePlaceholder,
  type StepSelection,
} from '@/lib/intent/build-intent';
import { actionCostToSlot, type BuiltIntent } from '@/lib/intent/build-from-card';
import type { ActionCard, ActionSlot } from '@danda/shared';
import { useGameStore } from '@/lib/stores/gameStore';

// =============================================================================
// Recently Used Commands (localStorage)
// =============================================================================

const RECENT_COMMANDS_KEY = 'polymyth:recentGameCommands';
const MAX_RECENT = 5;

function getRecentCommands(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_COMMANDS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentCommand(name: string) {
  try {
    const recent = getRecentCommands().filter((n) => n !== name);
    recent.unshift(name);
    localStorage.setItem(RECENT_COMMANDS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // Ignore localStorage errors
  }
}
import {
  type GameSlashCommand,
  type CompletionStepType,
  type FreeCastContext,
  GAME_COMMANDS,
  getSpellCommands,
  getAbilityCommands,
  buildEnemyOptions,
  buildAllyOptions,
  buildAllyOrSelfOptions,
  buildDyingAllyOptions,
  buildDeadAllyOptions,
  isReviveSpell,
  buildAllTargetOptions,
  buildFriendlyTargetOptions,
  buildSelfOnlyTargetOption,
  buildSceneCharOptions,
  buildSceneNpcOptions,
  buildPartyMemberOptions,
  buildSpellOptions,
  buildInventoryItemOptions,
  buildSceneItemOptions,
  buildSceneObjectOptions,
  buildOpenableObjectOptions,
  buildClosableObjectOptions,
  buildBreakableObjectOptions,
  buildUsableItemOptions,
  buildEquippableItemOptions,
  buildItemSpellOptions,
  buildItemAbilityOptions,
  buildTravelDestinationOptions,
  buildPoolSpendOptions,
  buildSpellSlotLevelOptions,
  buildTransmutedDamageTypeOptions,
  buildSlotLevelChoiceOptions,
} from '@/components/panels/game-commands';
import type { ItemTriggerData } from '@/lib/action-trigger';
import { buildCardSlashCommand, targetStepFor } from '@/components/panels/build-card-command';
import type {
  GameCharacterInfo,
  GameSpellcastingInfo,
  SceneItem,
  SceneInteractable,
  TravelDestination,
} from '@danda/shared';

// =============================================================================
// Structured-ability handlers — defensive mirror of the backend denylist
// =============================================================================

/**
 * Mirrors the backend denylist at
 * `backend/game-server/src/engine/mechanics_module/unified_handlers/handlers/use_ability/mod.rs:61`
 * and the `is_toggle_by_name` set at
 * `backend/shared/src/enricher/mechanics/action_cards/builders.rs:229`.
 *
 * Click-path fallback: when `permanent.action_cards` is missing (e.g. session
 * restore drops the cache and the lobby doesn't re-enrich, see Layer 2 task),
 * the action button looks up the card by label, misses, and falls back to
 * `triggerAbilityShortcut`. Without this mirror, the legacy path emits bare
 * `UseAbility{name}` which the backend denylist rejects → round aborts.
 *
 * Add new entries here whenever the backend denylist or toggle list grows.
 */
type StructuredAbilityKind =
  | { kind: 'flurry' }
  | { kind: 'mode'; mode_id: string }
  | { kind: 'toggle' }
  | { kind: 'passive' };

const STRUCTURED_ABILITY_HANDLERS: Record<string, StructuredAbilityKind> = {
  // Backend denylist — these MUST emit a structured shape, not bare UseAbility.
  'flurry of blows': { kind: 'flurry' },
  'cunning action': { kind: 'mode', mode_id: 'dash' },
  'step of the wind': { kind: 'mode', mode_id: 'free' },
  'patient defense': { kind: 'mode', mode_id: 'free' },
  // Passive / non-standalone — should never get a button (PASSIVE_EXCLUSIONS
  // in builders.rs filters them out before card emission). If one slips
  // through, suppress the click instead of letting the legacy path emit
  // bare UseAbility{name} and crash the round.
  // - `martial arts` is a passive on-hit rider.
  // - `metamagic` is a spell-time modifier (routes via
  //   CastSpellWithMetamagic), not a standalone activation.
  'martial arts': { kind: 'passive' },
  metamagic: { kind: 'passive' },
  // Toggles — `activate: true` matches the card-path UX (always activates on
  // click) and avoids the silent "flip current state" interpretation when
  // backend sees `None`.
  rage: { kind: 'toggle' },
  'innate sorcery': { kind: 'toggle' },
  'dragon wings': { kind: 'toggle' },
};

/**
 * Defensive-depth mirror of the Rust `PASSIVE_EXCLUSIONS` const at
 * `backend/shared/src/enricher/mechanics/action_cards/builders.rs:32`.
 *
 * The backend filter is authoritative — `card_from_ability` returns `None`
 * for any passive in that list so it never gets an ActionCard. This FE set
 * is a defense-in-depth fallback: if a passive slips into the legacy
 * click path (label-normalization miss, future denylist drift, session-
 * restore loses cards), `lookupStructuredHandler` finds it here and treats
 * it as `kind: 'passive'` → noop, instead of emitting bare
 * `UseAbility{name}` that the backend denylist would reject and the
 * dispatch catch-all (intent_dispatch.rs:700+) would turn into a
 * `RejectedIntent`.
 *
 * Historically the FE mirror had only 2 passive entries (Martial Arts,
 * Metamagic) vs the Rust side's 60+. Extended to match.
 *
 * KEEP IN SYNC with the Rust const. The Rust list is the source of truth;
 * this is a downstream copy.
 */
const PASSIVE_ABILITY_NAMES: ReadonlySet<string> = new Set([
  // Barbarian
  'danger sense',
  'brutal strike',
  'instinctive pounce',
  'feral instinct',
  'relentless rage',
  // Bard
  'font of inspiration',
  'expertise',
  'jack of all trades',
  // Cleric
  'divine order',
  'divine order: protector',
  'divine order: thaumaturge',
  // Druid
  'primal order',
  'primal order: magician',
  'primal order: warden',
  // Fighter
  'second wind heroic boost',
  'martial proficiency',
  'weapon mastery',
  'tactical mind',
  'tactical shift',
  'tactical master',
  'improved critical',
  'superior critical',
  'indomitable',
  // Monk (martial arts handled by STRUCTURED_ABILITY_HANDLERS above)
  'unarmored movement',
  'uncanny metabolism',
  'deflect attacks',
  'slow fall',
  'stillness of mind',
  'self-restoration',
  'perfect self',
  'body and mind',
  'purity of body',
  'diamond soul',
  'empty body',
  'evasion',
  'heightened focus',
  // Paladin
  'aura of protection',
  'aura of courage',
  'aura of devotion',
  'smite of protection',
  'radiant strikes',
  'sacred oath',
  'divine smite',
  'restoring touch',
  'faithful steed',
  'extra attack',
  // Ranger
  'favored enemy',
  'deft explorer',
  'roving',
  "hunter's mark",
  // Rogue
  'sneak attack',
  'reliable talent',
  'slippery mind',
  'elusive',
  'stroke of luck',
  'blindsense',
  'soul of deceit',
  'supreme sneak',
  // Sorcerer (metamagic handled by STRUCTURED_ABILITY_HANDLERS above)
  'sorcerous restoration',
  'arcane apotheosis',
  'sorcerous versatility',
  // Warlock
  'pact magic',
  'eldritch invocations',
  'contact patron',
  'mystic arcanum',
  'mystic arcanum (6th level)',
  'mystic arcanum (7th level)',
  'mystic arcanum (8th level)',
  'mystic arcanum (9th level)',
  'eldritch master',
  'pact boon',
  // Wizard
  'wizard spellcasting',
  'ritual adept',
  'scholar',
  'memorize spell',
  'spell mastery',
  'signature spells',
  // Species — Goliath
  "fire's burn",
  "frost's chill",
  "hill's tumble",
  "stone's endurance",
  "storm's thunder",
  // Species — other
  'darkvision',
  'fey ancestry',
  'trance',
  'keen senses',
  'mask of the wild',
  'lucky',
  'brave',
  'halfling nimbleness',
  'naturally stealthy',
  'stout resilience',
  // Gnome lineages (builders.rs:145-148)
  'gnome cunning',
  "artificer's lore",
  'tinker',
]);

function lookupStructuredHandler(abilityName: string): StructuredAbilityKind | undefined {
  const lower = abilityName.toLowerCase();
  const handler = STRUCTURED_ABILITY_HANDLERS[lower];
  if (handler) return handler;
  // Defense-in-depth: anything on the Rust PASSIVE_EXCLUSIONS list that
  // slips through the click path gets treated as a noop instead of
  // emitting bare `UseAbility{name}`.
  if (PASSIVE_ABILITY_NAMES.has(lower)) {
    return { kind: 'passive' };
  }
  return undefined;
}

// =============================================================================
// Stage Types
// =============================================================================

type SlashStage =
  | { type: 'idle' }
  | { type: 'command_select'; filter: string }
  | {
      type: 'option_select';
      command: GameSlashCommand;
      stepIndex: number;
      stepType: CompletionStepType;
      baseText: string;
      filter: string;
      /** Accumulated selections from previous steps */
      selections: StepSelection[];
      /**
       * Multi-select state for this step. Present when the step allows
       * picking >1 option (e.g. /cast Bless's target step, where SRD lets the
       * caster pick up to 3 creatures). The hook:
       * - prepends a synthetic "Confirm (N of M)" pseudo-item to the menu,
       * - on each `handleSelect(option)` call toggles the option in
       *   `selectedItems` (does NOT advance the stage),
       * - advances when the user picks the Confirm pseudo-item (or when
       *   `selectedItems.length === max` and they pick the last option —
       *   future polish; today the player explicitly confirms),
       * - rolls the final array into the next `StepSelection.selectedItems`.
       *
       * Absent for single-select steps — those keep the old "pick one →
       * advance immediately" behaviour, no UX regression.
       */
      multiSelect?: {
        /** Max number of selections (Bless: 3 at L1, +1 per upcast slot). */
        max: number;
        /** Currently checked items, in pick order. */
        selectedItems: SlashCommandDefinition[];
      };
    }
  | { type: 'done' };

// `StepSelection` lives in `@/lib/intent/build-intent` so the menu state and
// the Intent constructor share one definition.

// =============================================================================
// Game Context (data sources for menus)
// =============================================================================

export interface GameCommandContext {
  inCombat: boolean;
  partyMembers: GameCharacterInfo[];
  sceneCharacters: GameCharacterInfo[];
  playerCharacterId: string | null;
  sceneItems: SceneItem[];
  sceneInteractables: SceneInteractable[];
  /** Player character's spellcasting info */
  spellcasting?: GameSpellcastingInfo;
  spellSlotsUsed?: Record<number, number>;
  /**
   * Mirrors `SpellMechanicsSummary` (in `game-commands.ts`). The `effect`
   * field is what `computeMultiSelectMaxForStep` reads to detect multi-
   * target buffs (Bless: `effect.type === 'buff'` + `max_targets > 1`).
   * Without `effect` typed on the surface, the helper's parameter type
   * would still accept the narrower shape via TS structural assignability
   * — multi-select would happen to work at runtime because the real value
   * is `SpellData[]` (carrying `effect`) — but a future boundary narrowing
   * would silently break it. Type the field explicitly.
   */
  spellMechanics?: Array<{
    name: string;
    base_level: number;
    effect?: SpellEffect;
    /** SRD 5.2 Twinned Spell eligibility. Read by the `twinned_target`
     *  builder — when the picked spell is not twinnable, the builder
     *  returns `[]` so the empty-state bubble "Can't twin this spell."
     *  fires and the wizard resets. Defaults to `false` for legacy
     *  fixtures that predate the field. */
    twinnable?: boolean;
    /** SRD casting time. Drives the reaction-spell filter in
     *  `buildSpellOptions` — spells with `'reaction'` are hidden from
     *  the proactive `/cast` menu (they auto-fire via the engine's
     *  reaction subscription). */
    casting_time?: string;
    /** Backend-derived target audience (effect + range). Drives the runtime
     *  substitution in the `all_targets` step so `/cast`, per-spell shortcuts,
     *  Ctrl+N, and metamagic all curate their target roster to the spell
     *  (Fireball → enemies, Bless → friendly, Mage Armor → self). Absent on
     *  legacy fixtures ⇒ `all_targets` fallback (no regression). */
    target_audience?: SpellTargetAudience;
  }>;
  /** Player character's prepared spells. Level 0 = cantrip, 1-9 = leveled (D&D convention).
   *  Used as both filter (for spells_known_by_level) and direct source (for prepared casters). */
  preparedSpells?: { name: string; level: number }[];
  /** Player character's equipment (equipped items only — for equip/unequip menus) */
  equipment?: GameCharacterInfo['equipment'];
  /** Player character's full inventory (all items — for give/drop menus) */
  inventory?: GameCharacterInfo['equipment'];
  /**
   * Player character's computed `action_cards` — **the single source of
   * truth** for ability-based slash commands (formerly `abilityMechanics`).
   * Drives `/ability`-style autocomplete in parallel with the Actions panel
   * buttons, so click and chat paths share one data source.
   */
  actionCards?: import('@danda/shared').ActionCard[];
  /** Reachable travel destinations for /travel command */
  travelDestinations?: TravelDestination[];
  /** Innate spell free-cast quota context (species / Magic Initiate).
   *  When provided, exhausted innate spells are filtered from the /cast menu
   *  and Ctrl+N shortcut list. Build via `buildFreeCastContext(permanent, session)`. */
  freeCastContext?: FreeCastContext;
  /** Spell name the player is currently concentrating on, or `undefined`/`null`
   *  when not concentrating. Used to gate `/end-conc` visibility — firing the
   *  slash with no active spell sends an intent the BE silently no-ops, but
   *  the FE shouldn't expose a do-nothing command in the menu. */
  playerConcentration?: string | null;
  /** Per-card pool-remaining for pool-spend abilities (Paladin Lay on Hands).
   *  Keyed by `card.id`. Without this, the typed `/lay-on-hands` flow renders
   *  the pool-spend picker with `meta.poolRemaining = undefined → 0 → "Pool
   *  empty"` and the player ends up firing the intent with `pool_spend: None`
   *  (engine defaults to 1 HP from a 15-HP pool — useless heal). The click
   *  path in CharDetailsPanel passes this via `triggerCardShortcut(card,
   *  { poolRemaining })`; the typed-slash path needs the same data injected
   *  here so `getAbilityCommands` can pre-populate `meta.poolRemaining` on
   *  each pool-card command at build time. */
  cardPoolRemaining?: Record<string, number>;
  /** Card ids whose resource is spent — hidden from the slash menu. */
  spentCardIds?: ReadonlySet<string>;
  /** Player character's class string (snake_case, matches Rust `ClassName`
   *  serde — e.g. `"bard"`). Used by class-gated slash commands like
   *  `/font-of-inspiration` to hide them for the wrong class. Backend is
   *  authoritative; this is UX-only. */
  playerClass?: string;
  /** Player character's total level. Used as fallback for class-gated slash
   *  commands when `playerClassFeatures` is unhydrated. Backend remains
   *  authoritative. */
  playerLevel?: number;
  /** Player character's `permanent.class_features` (name-only is enough).
   *  Authoritative FE signal for feature-gated slash commands — mirrors the
   *  backend handler's `class_features` check (multiclass-aware). When
   *  present, takes precedence over `playerClass + playerLevel` fallback. */
  playerClassFeatures?: Array<{ name: string }>;
}

// =============================================================================
// Hook Return Type
// =============================================================================

export interface GameSlashCommandState {
  /** Items to show in the slash menu */
  menuItems: SlashCommandDefinition[];
  /** Whether the slash menu should be shown */
  showMenu: boolean;
  /** Currently selected index in the menu */
  selectedIndex: number;
  /** Current stage label (e.g., "Select target") */
  stageLabel: string | null;
  /** Whether we're in a multi-step flow */
  isMultiStep: boolean;
  /** Whether the state machine is active (command_select or option_select — for empty state) */
  isActive: boolean;

  /** Handle text input change — call this when activeMessage changes.
   *  May return `next_step` when the typed filter exactly matches a multi-step
   *  command and auto-advances the state machine into its option picker — the
   *  caller must apply the result (e.g. via `applyShortcutResult`) so the chat
   *  input text is rewritten to `baseText`. Otherwise returns `noop`. */
  handleTextChange: (text: string) => SlashSelectResult;
  /** Handle selection of a menu item */
  handleSelect: (item: SlashCommandDefinition) => SlashSelectResult;
  /** Handle Esc key — go back one step */
  handleBack: () => SlashBackResult;
  /** Set selected index */
  setSelectedIndex: (index: number) => void;

  /** Current typed Intent envelope (null when free text or incomplete;
   *  built up during multi-step). Carries `intent`, `action_slot`, and
   *  optional `turn_flags`. */
  built: BuiltIntent | null;
  /** Backward-compat shorthand for `built?.intent ?? null`. Tests + chat
   *  callers that only need the inner `Intent` (and don't care about the
   *  slot envelope) read this. New code should read `built` directly. */
  intent: Intent | null;
  /** Reset state (e.g., after sending message) */
  reset: () => void;
  /**
   * Client-side resolver invoked on submit when the user pressed Enter on
   * unresolved slash input (e.g., `/use Potion of Healing` typed raw, or
   * `/use` auto-advanced into the picker but sent without clicking). Returns a
   * templated `text` + typed `built` envelope when the input maps unambiguously
   * to a known command + option(s); returns null when no match is possible
   * (caller should fall back to plain text). Does not touch hook state — the
   * chat submit path calls `reset()` after sending as usual.
   *
   * Most callers should use `finalizeForSubmit` instead, which folds
   * `resolveFromSubmit` together with the menu-flow result + free-text
   * comparison.
   */
  resolveFromSubmit: (
    text: string
  ) => { text: string; built: BuiltIntent | null; intent: Intent | null } | null;
  /**
   * Submit-time finalizer — single source of truth for the typed-Intent vs
   * free-text routing decision. Combines `resolveFromSubmit` with the last
   * completed menu-flow result, then compares the user's submitted text to
   * the canonical templated text (case-sensitive, trimmed both sides). When
   * the submitted text is a pure menu pick, returns the typed Intent so the
   * caller ships `player_intent`. When the user deviated from the template
   * (free-text suffix beyond `Say: `, edited target name, etc.), returns
   * `intent: null` so the caller ships `player_text` and the LLM agent
   * builds the Intent from prose.
   *
   * Returns one of:
   *  - **Pure menu / structured intent** (`built` populated) — caller ships
   *    `player_intent` with `built.intent`, `built.action_slot`,
   *    `built.turn_flags`. Backend skips the LLM agent.
   *  - **Free-text deviation or pure free typing** (`built: null`) — caller
   *    ships `player_text`, backend routes through the LLM agent.
   */
  finalizeForSubmit: (text: string) => {
    sendText: string;
    built: BuiltIntent | null;
    /** Backward-compat shorthand for `built?.intent ?? null`. */
    intent: Intent | null;
  };
  /** Programmatically trigger a spell shortcut (Ctrl+N / Actions panel) — returns
   *  fill result or enters target flow. `freeCast: true` is required for non-caster
   *  innate/MI leveled spells that have no spell-slot table; otherwise
   *  `slot_level_choice` falls into the slot-paid branch with zero options.
   */
  triggerSpellShortcut: (
    spellName: string,
    spellLevel: number,
    freeCast?: boolean
  ) => SlashSelectResult;
  /** Programmatically trigger a weapon attack shortcut — enters target selection */
  triggerWeaponAttackShortcut: (weaponName: string) => SlashSelectResult;
  /** Programmatically trigger an ability shortcut — enters target selection or sends immediately for self-only.
   *  `runtime.poolRemaining` is required for pool-spend abilities (Lay on Hands) when
   *  the action_cards layer hasn't been populated yet (post-session-restore). Without
   *  this hook the legacy fallback emits `pool_spend: None` and the engine defaults to
   *  1 HP — see #275. */
  triggerAbilityShortcut: (
    abilityName: string,
    targetType?: 'self_only' | 'targeted',
    runtime?: { poolRemaining?: number }
  ) => SlashSelectResult;
  /** Programmatically trigger an item shortcut — enters appropriate flow based on effect type */
  triggerItemShortcut: (data: ItemTriggerData) => SlashSelectResult;
  /** Card-aware trigger — dispatches an ActionCard click through the
   *  appropriate flow (target picker if needed, direct-send otherwise).
   *  Uses `build-from-card.ts` so the resulting Intent carries
   *  `flurry_of_blows`, `mode_id`, `metamagic`, etc. exactly as the enricher
   *  expects. `runtime.poolRemaining` is required for pool-spend cards
   *  (Paladin Lay on Hands) so the amount picker can cap options at the
   *  current pool remaining. */
  triggerCardShortcut: (
    card: import('@danda/shared').ActionCard,
    runtime?: { poolRemaining?: number }
  ) => SlashSelectResult;
}

export type SlashSelectResult =
  | {
      action: 'fill';
      text: string;
      built: BuiltIntent | null;
      /** Backward-compat shorthand for `built?.intent ?? null`. */
      intent: Intent | null;
      done: boolean;
    }
  | { action: 'next_step'; baseText: string }
  /** Pool-spend custom-input request. Consumer opens a numeric Dialog
   *  in the range [1, poolMax]. On confirm, the consumer synthesizes a
   *  `SlashCommandDefinition` carrying `meta.poolSpend = <picked>` and
   *  feeds it back through `handleSelect`. Triggered by the "Other
   *  amount…" sentinel row on the `pool_spend` step. */
  | { action: 'request_pool_input'; poolMax: number }
  /** Reset the wizard to idle and clear the chat input. Used by per-spell
   *  shortcut Cancel — there's no prior `spells` step to rewind to, so the
   *  picker exits cleanly instead of freezing on a no-op. */
  | { action: 'clear' }
  | { action: 'noop' };

export type SlashBackResult =
  | { action: 'clear' }
  | { action: 'back_to_commands'; text: string }
  | { action: 'back_to_step'; text: string }
  | { action: 'noop' };

// =============================================================================
// Submit-time option matcher
// =============================================================================

/**
 * Resolve a user-entered filter string against a step's options. Used at submit
 * time to convert `/use Potion of Healing`-style raw text (or an unsent
 * option_select filter) into a concrete selection, so the chat send path can
 * build a proper intent hint instead of shipping the raw slash line.
 *
 * Order: exact case-insensitive name match → unambiguous case-insensitive
 * prefix match. Returns null on no match or prefix ambiguity so the caller
 * falls back to plain chat.
 */
function findUnambiguousOptionMatch(
  options: SlashCommandDefinition[],
  filter: string
): SlashCommandDefinition | null {
  const needle = filter.trim().toLowerCase();
  if (!needle) return null;
  const selectable = options.filter((o) => !(o.isMenuOption && o.sourceId == null));
  // Exact name match wins. Then exact sourceId match — players (and scenarios)
  // sometimes paste canonical ids (`/travel village_square`,
  // `/attack skeleton_1_8af2`) and the bubble + backend should resolve those
  // to the display name so chat reads naturally instead of leaking the id.
  const exactName = selectable.find((o) => o.name.toLowerCase() === needle);
  if (exactName) return exactName;
  const exactId = selectable.find((o) => o.sourceId?.toLowerCase() === needle);
  if (exactId) return exactId;
  const prefixMatches = selectable.filter((o) => o.name.toLowerCase().startsWith(needle));
  if (prefixMatches.length === 1) return prefixMatches[0];
  return null;
}

/**
 * Extract inter-placeholder literal separators from a textTemplate so typed
 * multi-step commands like `/give Dagger to bram` can be split into per-step
 * arguments at submit time.
 *
 * `'Give {item} to {target}'` → `[' to ']`
 * `'Cast {spell} at {target}'` → `[' at ']`
 * `'Travel to {destination}'` → `[]` (single placeholder, no inter-literal)
 *
 * Leading text before the first placeholder (the verb, e.g. `Give `) is
 * discarded — the typed-command path has already stripped the verb when it
 * split on the leading slash. Trailing text after the last placeholder
 * (rare) is likewise dropped from the separator list.
 */
function parseTemplateSeparators(template: string): string[] {
  const parts = template.split(/\{[^}]+\}/);
  // `split` returns `[before-first, between-1-2, between-2-3, …, after-last]`.
  // Inter-placeholder separators are everything except the first and last.
  if (parts.length <= 2) return [];
  return parts.slice(1, -1);
}

/**
 * Split a typed multi-step `remainder` (post-`/cmd `) into per-step segments
 * by locating each `textTemplate` separator in order. Returns `null` if any
 * separator is missing — the caller falls back to single-step matching so
 * partial inputs (e.g. `/give Dagger` without a target) still open the menu.
 *
 * Splits at the FIRST occurrence of each separator. Item / target names that
 * contain a preposition would get split on the leading occurrence, which is
 * the wrong boundary. Inventory items in Goldvale don't carry " to "/" at "/
 * " on " substrings; travel destinations DO (e.g. "Mountain Path to Dragon
 * Lair") but `/travel` is single-step so the multi-step split path is never
 * reached for them. First-occurrence is safe for every multi-step + textTemplate
 * command currently wired (`/give`, `/cast`, `/use`); revisit this if a future
 * multi-step command consumes options whose names contain those substrings.
 */
function splitMultiStepRemainder(remainder: string, separators: string[]): string[] | null {
  if (separators.length === 0) return [remainder.trim()].filter((s) => s.length > 0);
  let rest = remainder;
  const segments: string[] = [];
  for (const sep of separators) {
    const lowered = rest.toLowerCase();
    const idx = lowered.indexOf(sep.toLowerCase());
    if (idx === -1) return null;
    segments.push(rest.slice(0, idx).trim());
    rest = rest.slice(idx + sep.length);
  }
  segments.push(rest.trim());
  return segments.every((s) => s.length > 0) ? segments : null;
}

/**
 * Extract the literal text between the command verb and the first
 * `{placeholder}` in a textTemplate so typed `/cmd <literal> <arg>` forms
 * resolve naturally. `'Travel to {destination}'` with cmdName `'travel'` →
 * `'to '`; `'Break the {object}'` with cmdName `'break'` → `'the '`. Returns
 * empty string when the placeholder is adjacent to the verb (`'Help {target}'`)
 * or when the template's leading words don't unambiguously match cmdName
 * (defensive: never strips when the mapping is uncertain).
 *
 * Multi-word cmd-verb forms like `/pickup` against template `'Pick up {item}'`
 * are handled by consuming cmdName one character at a time across spaces.
 */
function parseTemplatePrefixLiteral(template: string, cmdName: string): string {
  const firstBrace = template.indexOf('{');
  if (firstBrace === -1) return '';
  const beforeBrace = template.slice(0, firstBrace);
  const cmdLower = cmdName.toLowerCase();
  let consumed = 0;
  let cmdIdx = 0;
  while (cmdIdx < cmdLower.length && consumed < beforeBrace.length) {
    const ch = beforeBrace[consumed];
    if (ch === ' ') {
      consumed++;
      continue;
    }
    if (ch.toLowerCase() === cmdLower[cmdIdx]) {
      consumed++;
      cmdIdx++;
    } else {
      // Template's leading text doesn't match cmdName — defensive bail.
      return '';
    }
  }
  // Skip trailing whitespace after the consumed verb chars.
  while (consumed < beforeBrace.length && beforeBrace[consumed] === ' ') consumed++;
  return beforeBrace.slice(consumed);
}

/**
 * Recognises the natural-language equivalents of `/pickup-all` so a player
 * typing `/pickup all`, `/pickup everything`, etc. routes to the backend
 * `__all__` sentinel that enumerates every lootable scene item.
 *
 * The backend `enrich_pickup` accepts the same set as a defence-in-depth so
 * the LLM-emitted natural-language path also lands on the sentinel.
 */
function isPickupAllPhrase(remainder: string): boolean {
  switch (remainder.trim().toLowerCase()) {
    case 'all':
    case 'all loot':
    case 'all the loot':
    case 'all items':
    case 'all the items':
    case 'everything':
    case 'everything in the scene':
      return true;
    default:
      return false;
  }
}

// =============================================================================
// Pre-flight availability check
// =============================================================================

/**
 * Compute the option count for a step using only context that's available
 * before the user picks anything. Returns the count for steps in
 * `STATIC_CONTEXT_STEPS` and -1 for context-dependent steps (caller skips
 * them and defers to the runtime chat-bubble fallback).
 */
function staticOptionCount(
  step: CompletionStepType,
  ctx: GameCommandContext,
  cmdMeta: Record<string, unknown> | undefined
): number {
  if (!STATIC_CONTEXT_STEPS.has(step)) return -1;
  switch (step) {
    case 'enemies':
      return buildEnemyOptions(ctx.sceneCharacters).length;
    case 'allies':
      return buildAllyOptions(ctx.partyMembers, ctx.playerCharacterId).length;
    case 'allies_or_self':
      return buildAllyOrSelfOptions(ctx.partyMembers, ctx.playerCharacterId).length;
    case 'dying_allies':
      return buildDyingAllyOptions(ctx.partyMembers, ctx.playerCharacterId).length;
    case 'dead_allies':
      return buildDeadAllyOptions(ctx.partyMembers, ctx.playerCharacterId).length;
    case 'all_targets':
      return buildAllTargetOptions(ctx.partyMembers, ctx.sceneCharacters, ctx.playerCharacterId)
        .length;
    case 'friendly_targets':
      return buildFriendlyTargetOptions(
        ctx.partyMembers,
        ctx.sceneCharacters,
        ctx.playerCharacterId
      ).length;
    case 'scene_chars':
      return buildSceneCharOptions(ctx.partyMembers, ctx.sceneCharacters, ctx.playerCharacterId)
        .length;
    case 'scene_npcs':
      return buildSceneNpcOptions(ctx.sceneCharacters).length;
    case 'party_members':
      return buildPartyMemberOptions(ctx.partyMembers, ctx.playerCharacterId).length;
    case 'spells':
      return buildSpellOptions(
        ctx.spellcasting,
        ctx.spellSlotsUsed,
        undefined,
        ctx.spellMechanics,
        ctx.preparedSpells,
        ctx.freeCastContext
      ).length;
    case 'inventory_items':
      // Mirror the runtime picker filters so /give (excludeQuestItems)
      // and /drop (excludeWondrous) don't show in the slash menu when
      // the FILTERED inventory is empty — otherwise the command surfaces
      // then opens an empty picker (e.g. char carrying only the Amulet
      // of Theron sees /give, picks recipient, opens to nothing).
      return buildInventoryItemOptions(ctx.inventory ?? ctx.equipment, undefined, {
        excludeQuestItems: cmdMeta?.excludeQuestItems === true,
        excludeWondrous: cmdMeta?.excludeWondrous === true,
      }).length;
    case 'usable_items':
      return buildUsableItemOptions(ctx.inventory ?? ctx.equipment).length;
    case 'equippable_items':
      return buildEquippableItemOptions(ctx.inventory ?? ctx.equipment).length;
    case 'scene_items':
      return buildSceneItemOptions(ctx.sceneItems).length;
    case 'scene_objects':
      return buildSceneObjectOptions(ctx.sceneInteractables).length;
    case 'openable_objects':
      return buildOpenableObjectOptions(ctx.sceneInteractables).length;
    case 'closable_objects':
      return buildClosableObjectOptions(ctx.sceneInteractables).length;
    case 'breakable_objects':
      return buildBreakableObjectOptions(ctx.sceneInteractables).length;
    case 'travel_destinations':
      return buildTravelDestinationOptions(ctx.travelDestinations).length;
    case 'spell_slot_levels':
      return buildSpellSlotLevelOptions(ctx.spellcasting?.max_spell_slots, ctx.spellSlotsUsed)
        .length;
    case 'pool_spend': {
      const remaining = (cmdMeta?.poolRemaining as number | undefined) ?? 0;
      return buildPoolSpendOptions(remaining).length;
    }
    case 'transmuted_damage_type':
      // Context-dependent: the picker filters by the picked spell's
      // damage type so the count is 5 (filtered) or 6 (non-damaging
      // spell). Skip pre-flight; the runtime empty-state guards.
      return -1;
    default:
      return -1;
  }
}

/**
 * Pre-flight check: walk each static-context step in `cmd.completionSteps`
 * and require >0 options for the command to surface. Skips context-
 * dependent steps (item_spells / item_abilities) — those rely on the
 * runtime empty-state bubble.
 *
 * `/use`'s `friendly_targets` step is conditional on the item kind picked
 * in step 0 (potions chain, other items finalize). Skip the pre-flight
 * for that step so /use stays visible when the player has scrolls/tools
 * even if no friendly targets are around.
 */
function commandIsAvailable(cmd: GameSlashCommand, ctx: GameCommandContext): boolean {
  const steps = cmd.completionSteps ?? [];
  for (const step of steps) {
    if (cmd.name === 'use' && step === 'friendly_targets') continue;
    const count = staticOptionCount(step, ctx, cmd.meta);
    if (count === -1) continue;
    if (count === 0) return false;
  }
  return true;
}

// =============================================================================
// Empty-state chat bubbles
// =============================================================================

/**
 * Per-step copy used when an option builder returns zero rows mid-flow.
 * The slash machine dispatches this as a local italic chat bubble
 * (`gameStore.addLocalBubble`) instead of opening an empty picker, then
 * resets so the player can pick another command. The pre-flight filter in
 * `availableCommands` already hides commands whose static steps are empty —
 * these strings only fire when state shifted between menu-open and step
 * arrival, or when a context-dependent step (item_spells, item_abilities)
 * is empty after a prior pick.
 */
const EMPTY_STATE_BUBBLE: Record<CompletionStepType, string> = {
  enemies: 'No enemies in sight.',
  allies: 'No allies nearby.',
  allies_or_self: 'No allies nearby.',
  dying_allies: 'No dying allies to help.',
  dead_allies: 'No fallen allies to revive.',
  all_targets: 'No valid targets here.',
  friendly_targets: 'No valid targets here.',
  scene_chars: 'No valid targets here.',
  scene_npcs: 'No NPCs nearby to invite.',
  party_members: 'No party members to direct.',
  spells: 'No spell ready to cast.',
  inventory_items: 'Your pack is empty.',
  usable_items: 'Nothing usable in your pack.',
  equippable_items: 'Nothing to equip.',
  scene_items: 'Nothing to pick up here.',
  scene_objects: 'Nothing to investigate here.',
  openable_objects: 'Nothing to open here.',
  closable_objects: 'Nothing to close here.',
  breakable_objects: 'Nothing here to break.',
  item_spells: 'No spells loaded in this item.',
  item_abilities: 'No usable abilities on this item.',
  travel_destinations: 'No paths from here.',
  pool_spend: 'Your pool is empty.',
  spell_slot_levels: 'No spell slots remaining.',
  // Six fixed options — zero is unreachable.
  transmuted_damage_type: 'No damage type available.',
  // The builder always returns at least the base level for any leveled
  // spell the player picked (the cast-eligibility filter upstream
  // guarantees the base slot is available); the cantrip / free-cast
  // arms return a single "no slot needed" option. So this bubble is
  // unreachable in practice but kept for the exhaustive map.
  slot_level_choice: 'No spell slot to spend.',
  // Twinned Spell second-target step fires this bubble when the picked
  // spell isn't twinnable (the builder returns []) — covers both the
  // "spell is AoE / multi-target" case and the "no valid second target
  // in scene" case (rare; usually a creature is around when casting).
  twinned_target: "Can't twin this spell.",
  // Careful Spell exclude step fires this bubble when no allies are in
  // the spell's AoE (nothing to exclude). The empty list is the right
  // outcome — auto-pick continues to cast — so this bubble is unreachable
  // in practice.
  careful_exclude: 'No allies in the area to shield.',
  // Bestow Curse 4-effect picker — four fixed options, never empty.
  bestow_curse_effect: 'No curse effect available.',
  // Ability sub-picker — six fixed options (STR/DEX/CON/INT/WIS/CHA), never empty.
  ability_picker: 'No ability available.',
  // Concentration swap confirm — two fixed options (Continue/Cancel), never
  // empty when the step is reached (skipped via shouldSkipNextStep when not
  // applicable).
  concentration_confirm: 'No concentration conflict.',
};

/** Steps whose option count depends only on context (not on prior selections).
 *  Pre-flight walks these to decide whether to hide a command from the menu;
 *  context-dependent steps (item_spells/item_abilities, pool_spend pre-card-
 *  injection) skip the walk and rely on the runtime chat-bubble fallback. */
const STATIC_CONTEXT_STEPS: ReadonlySet<CompletionStepType> = new Set<CompletionStepType>([
  'enemies',
  'allies',
  'allies_or_self',
  'dying_allies',
  'dead_allies',
  'all_targets',
  'friendly_targets',
  'scene_chars',
  'scene_npcs',
  'party_members',
  'spells',
  'inventory_items',
  'usable_items',
  'equippable_items',
  'scene_items',
  'scene_objects',
  'openable_objects',
  'closable_objects',
  'breakable_objects',
  'travel_destinations',
  'spell_slot_levels',
  // `transmuted_damage_type` is context-dependent now: the picker reads
  // the spell pick's damage_type and filters the no-op (Fire→Fire),
  // so the option count depends on which spell was selected. Pre-flight
  // skips it via the -1 sentinel.
  // pool_spend builder reads `commandMeta.poolRemaining`, which is baked
  // into the command at construction time (set by `triggerCardShortcut` for
  // clicks and by `getAbilityCommands` for the slash menu). So the option
  // count is determinable at availability-check time too.
  'pool_spend',
]);

/** Steps that may auto-commit when they have exactly ONE option, skipping the
 *  picker. Deliberately tiny (#345) — only the combat-target shortcut plus
 *  mechanical/forced steps where a lone option is no real choice:
 *  - `enemies`: combat target. A single in-scene hostile auto-targets so combat
 *    stays fast (attack AND hostile spell/ability targeting in a 1-enemy scene).
 *  - `slot_level_choice` / `spell_slot_levels`: slot pickers. With a single
 *    legal slot (cantrips, free-cast, an L1 caster's only slot) there's no
 *    choice.
 *  - `pool_spend`: Lay on Hands HP amount. A 1-HP pool has exactly one
 *    spendable amount, so there's nothing to pick.
 *
 *  EVERY other step is a player-facing CHOICE picker (travel destinations, scene
 *  items, spells, friendly targets, objects, ...) and MUST open the picker even
 *  with one option — a stray submit must never silently commit a consequential
 *  action like travelling out of an encounter. */
const AUTO_PICK_SINGLE_OPTION_STEPS: ReadonlySet<CompletionStepType> = new Set<CompletionStepType>([
  'enemies',
  'slot_level_choice',
  'spell_slot_levels',
  'pool_spend',
]);

// =============================================================================
// Step Type Metadata
// =============================================================================

const STEP_LABELS: Record<CompletionStepType, string> = {
  enemies: 'Select a target',
  allies: 'Select an ally',
  // "target" not "ally" — the picker can include self, and labelling
  // "Select an ally" while the only option is "Self" reads as a UX bug.
  allies_or_self: 'Select a target',
  dying_allies: 'Select an ally',
  dead_allies: 'Select a fallen ally',
  all_targets: 'Select a target',
  friendly_targets: 'Select a target',
  spells: 'Select a spell',
  inventory_items: 'Select an item',
  usable_items: 'Select an item',
  equippable_items: 'Select an item',
  scene_items: 'Select an item',
  scene_objects: 'Select an object',
  openable_objects: 'Select an object',
  closable_objects: 'Select an object',
  breakable_objects: 'Break what?',

  scene_chars: 'Select a character',
  scene_npcs: 'Select a character',
  party_members: 'Select a character',
  item_spells: 'Select a spell',
  item_abilities: 'Select an ability',
  travel_destinations: 'Where to?',
  pool_spend: 'Heal how much?',
  spell_slot_levels: 'Expend which slot?',
  transmuted_damage_type: 'Reshape into what?',
  slot_level_choice: 'Cast at which level?',
  twinned_target: 'Twin the spell at…',
  careful_exclude: 'Shield which allies?',
  bestow_curse_effect: 'Choose the curse',
  ability_picker: 'Which ability?',
  concentration_confirm: 'End current concentration?',
};

// =============================================================================
// Multi-select Helpers
// =============================================================================

/** Synthetic sourceId for the "Confirm" pseudo-item the hook prepends to a
 *  multi-select stage. `handleSelect` matches this id to finalise the stage
 *  instead of advancing on the picked option. */
const MULTI_SELECT_CONFIRM_ID = '__multi_select_confirm__';

/**
 * Resolve the name of the spell being cast in the current flow, for looking up
 * its `target_audience`. Two sources, in priority order:
 *
 *  1. The most recent `spells` / `item_spells` selection in the chain — covers
 *     `/cast`, metamagic, and item multi-spell flows where the spell is picked
 *     in an earlier step.
 *  2. `commandMeta.castSpellName` (source-cast cards like Magic Initiate, which
 *     have no `spells` step — stamped in `build-card-command.ts`) or
 *     `commandMeta.spellName` (single-spell magic items like Wand of Fireball,
 *     stamped in `triggerItemShortcut`).
 *
 * Returns `null` when no spell can be resolved — callers fall back to the full
 * `all_targets` roster (no regression).
 */
function resolvePickedSpellName(
  priorSelections: StepSelection[] | undefined,
  commandMeta: Record<string, unknown> | undefined
): string | null {
  if (priorSelections) {
    for (let i = priorSelections.length - 1; i >= 0; i--) {
      const sel = priorSelections[i];
      if (sel.stepType === 'spells' || sel.stepType === 'item_spells') {
        const name = sel.selectedItem.sourceId ?? sel.selectedItem.name;
        if (name) return name;
        break;
      }
    }
  }
  const castName = commandMeta?.castSpellName as string | undefined;
  if (castName) return castName;
  const itemSpellName = commandMeta?.spellName as string | undefined;
  return itemSpellName ?? null;
}

/**
 * If the previous step in `selections` was a spell pick AND the picked spell
 * is a multi-target buff (Bless and friends — `SpellEffect::Buff` with
 * `max_targets > 1`), return the effective max for the *current* step (which
 * is assumed to be a target picker following the spell step). Includes
 * upcast scaling: `max_targets + extra_targets_per_level × (slot - base_level)`.
 *
 * Returns `null` when:
 *   - The previous step wasn't `spells` / `item_spells` (so we don't know
 *     which spell is being cast).
 *   - The picked spell has no `effect`, isn't a buff, or has no
 *     `max_targets` set (single-target spells — Shield, Cure Wounds, etc.).
 *   - The spell can't be resolved in `spellMechanics` (defensive fallback).
 *
 * Today this is wired for `cast_spell`; the helper is intentionally generic
 * so a future ability with a multi-target picker (e.g. Mass Healing Word
 * routed through `use_ability`) can reuse it.
 */
function computeMultiSelectMaxForStep(
  currentStepType: CompletionStepType,
  selections: StepSelection[],
  spellMechanics:
    | Array<{ name: string; base_level: number; effect?: SpellEffect; twinnable?: boolean }>
    | undefined,
  partyMemberCount: number = 0
): number | null {
  // Careful Spell exclude-list step — multi-select up to the party
  // member count. Backend validator narrows this to `min(CHA mod,
  // affected allies)` so over-inclusion silently truncates.
  if (currentStepType === 'careful_exclude') {
    return Math.max(1, partyMemberCount);
  }

  // The multi-select policy is currently spell-target only. Bail early on
  // step types that obviously can't multi-target (item pickers, slot pickers,
  // pool-spend amount, etc.) so we don't accidentally enable it on something
  // unrelated when callers extend selection chains.
  const isTargetStep =
    currentStepType === 'all_targets' ||
    currentStepType === 'allies' ||
    currentStepType === 'allies_or_self' ||
    currentStepType === 'friendly_targets' ||
    currentStepType === 'enemies';
  if (!isTargetStep) return null;

  // Find the most recent spell pick. With the slot-level-choice step
  // inserted between `spells` and `all_targets`, the IMMEDIATELY
  // previous selection at the target step is `slot_level_choice` —
  // walk backwards to find the actual spell-pick selection.
  let spellSel: (typeof selections)[number] | undefined;
  for (let i = selections.length - 1; i >= 0; i--) {
    const sel = selections[i];
    if (sel.stepType === 'spells' || sel.stepType === 'item_spells') {
      spellSel = sel;
      break;
    }
  }
  if (!spellSel) return null;
  const spellName = spellSel.selectedItem.sourceId ?? spellSel.selectedItem.name;
  if (!spellName) return null;

  const spell = spellMechanics?.find((s) => s.name === spellName);
  if (!spell?.effect) return null;
  if (spell.effect.type !== 'buff') return null;
  const baseMax = spell.effect.max_targets;
  if (baseMax === undefined || baseMax <= 1) return null;

  // Compute upcast scaling. Prefer the slot-level-choice step's pick
  // (canonical when /cast or per-spell shortcuts run through the new
  // 3-step flow). Fall back to the spell-step's meta (Ctrl+N legacy
  // path / pre-slot-picker tests) and finally to the spell's
  // `base_level`.
  let pickedLevel = spell.base_level;
  for (let i = selections.length - 1; i >= 0; i--) {
    const sel = selections[i];
    if (sel.stepType === 'slot_level_choice') {
      const raw = sel.selectedItem.meta?.slotLevel ?? sel.selectedItem.meta?.spellLevel;
      if (typeof raw === 'number') {
        pickedLevel = raw;
      }
      break;
    }
  }
  if (pickedLevel === spell.base_level) {
    const fallback = spellSel.selectedItem.meta?.spellLevel;
    if (typeof fallback === 'number') pickedLevel = fallback;
  }
  const upcastDelta = Math.max(0, pickedLevel - spell.base_level);
  const extraPerLevel = spell.effect.extra_targets_per_level ?? 0;
  return baseMax + extraPerLevel * upcastDelta;
}

/**
 * Build the "Confirm (N of M)" pseudo-item the multi-select stage prepends
 * to its menu. The label flips between two states:
 *   - 0 selected → "Confirm (pick at least 1)" (disabled-feel, but still
 *     pickable — clicking it just no-ops; the user keeps picking)
 *   - 1..M selected → "Confirm (N of M selected)" — pick to finalize.
 *
 * The pseudo-item carries `sourceId = MULTI_SELECT_CONFIRM_ID` so
 * `handleSelect` recognises it independent of label/locale.
 */
function buildMultiSelectConfirmItem(selectedCount: number, max: number): SlashCommandDefinition {
  const description =
    selectedCount === 0
      ? `Pick at least 1 target (up to ${max})`
      : `Cast on ${selectedCount} of ${max} selected`;
  return {
    name: '✅ Confirm',
    description,
    isMenuOption: true,
    sourceId: MULTI_SELECT_CONFIRM_ID,
    isMultiSelectConfirm: true,
    // Visual: the confirm row is conceptually pre-selected once at least one
    // target is checked, but it isn't a target itself. Leave `isChecked`
    // unset; the renderer skips the checkbox column for the confirm row.
  };
}

/**
 * Decorate menu options with multi-select markers: mark each item that's
 * already in `selectedItems` with `isChecked: true` so the menu UI renders a
 * checkbox/checkmark on it. Items at the cap that aren't already selected
 * still render normally — clicking them tries to add and would bump capacity;
 * `handleSelect` enforces the cap.
 */
function applyMultiSelectMarkers(
  options: SlashCommandDefinition[],
  selectedItems: SlashCommandDefinition[]
): SlashCommandDefinition[] {
  const checkedIds = new Set(
    selectedItems.map((it) => it.sourceId ?? it.name).filter((v): v is string => !!v)
  );
  // Stamp `isChecked` on every row (true | false) so the renderer reserves
  // the checkbox column for unchecked rows too — without this, ticked rows
  // would render with a checkbox and unticked rows would not, causing the
  // names to shift sideways on every selection.
  return options.map((opt) => {
    const id = opt.sourceId ?? opt.name;
    const checked = !!id && checkedIds.has(id);
    return { ...opt, isChecked: checked };
  });
}

const COMMUNICATION_INTENT_TYPES: ReadonlySet<string> = new Set([
  'say',
  'shout',
  'emote',
  'talk_to',
  'whisper',
]);

/** Intent types whose slash chain ends with `endsWithFreeText: true` AND
 *  whose final body can be turned into a typed Intent at submit time. The
 *  finalizer's per-intent rebuild branch is the authoritative dispatcher —
 *  this set is the gate that decides which chains carry the rebuild metadata.
 *
 *  Communication intents (say/talk_to/whisper/shout/emote) use the typed body
 *  as the message; /givecoins uses it as the amount (parsed via
 *  `parseCoinAmount`). All ship `PlayerIntent` to the wire — never
 *  `PlayerText` — when the slash menu produced a known intent and target. */
const FREE_TEXT_REBUILDABLE_INTENT_TYPES: ReadonlySet<string> = new Set([
  ...COMMUNICATION_INTENT_TYPES,
  'give_coins',
]);

/**
 * For menu-pick commands that end with a free-text body, capture the intent
 * type + resolved target_id at finalize time so the submit-time finalizer
 * can extract the typed body and build a TYPED intent directly — no intent
 * converter LLM round-trip. Currently covers:
 *
 *   - `/say` / `/sayto` / `/whisper` / `/shout` / `/emote` → comm intents
 *     (`buildCommunicationIntent`)
 *   - `/givecoins`                                         → `IntentGiveCoins`
 *     (`buildGiveCoinsIntent` + `parseCoinAmount`)
 *
 * Returns null for any command that isn't a known free-text-rebuildable
 * menu — those commands fall through to `player_text` as before.
 */
function pendingFreeTextFlowFromMenu(
  command: GameSlashCommand,
  selections: StepSelection[]
): { intentType: string; targetId: string | null } | null {
  if (!command.endsWithFreeText || !command.intentType) return null;
  if (!FREE_TEXT_REBUILDABLE_INTENT_TYPES.has(command.intentType)) return null;
  // Target lives on `scene_chars` for comms (sayto/whisper) and on
  // `party_members` for `/givecoins`. Look in both — there's no overlap
  // between the two step types in any current slash chain.
  const targetSel = selections.find(
    (s) => s.stepType === 'scene_chars' || s.stepType === 'party_members'
  );
  const targetId = targetSel?.selectedItem.sourceId ?? null;
  return { intentType: command.intentType, targetId };
}

/** Backwards-compatible alias — older call sites named the factory
 *  `pendingFreeTextCommFromMenu` when only comm intents were covered.
 *  Re-export the generalized name so call sites stay readable. */
const pendingFreeTextCommFromMenu = pendingFreeTextFlowFromMenu;

// =============================================================================
// Hook
// =============================================================================

export function useGameSlashCommands(ctx: GameCommandContext): GameSlashCommandState {
  const [stage, setStage] = useState<SlashStage>({ type: 'idle' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [built, setBuilt] = useState<BuiltIntent | null>(null);
  // Track the base text for filtering in option_select stages
  const baseTextRef = useRef('');
  /**
   * The most recent canonical text + typed Intent envelope built from a
   * menu/shortcut flow. Read by `finalizeForSubmit` when the user submits
   * without going back through `resolveFromSubmit` (the common case after
   * `triggerCardShortcut` or a clicked menu finalises). When the submitted
   * text deviates from `canonicalText`, the finalizer either rebuilds a
   * typed communication intent from `pendingFreeTextComm` (for endsWithFreeText
   * comm menus like /say, /sayto, /whisper) or drops the typed Intent and
   * routes through `player_text` so the LLM can reinterpret the body.
   */
  const lastBuildResultRef = useRef<{
    canonicalText: string;
    built: BuiltIntent | null;
    pendingFreeTextComm: { intentType: string; targetId: string | null } | null;
  } | null>(null);

  /** Commit a finalised intent envelope: state + ref together so
   *  `finalizeForSubmit` can detect free-text deviation at submit time. */
  const commitFinalResult = useCallback(
    (
      canonicalText: string,
      finalBuilt: BuiltIntent | null,
      pendingFreeTextComm: { intentType: string; targetId: string | null } | null = null
    ) => {
      setBuilt(finalBuilt);
      lastBuildResultRef.current = { canonicalText, built: finalBuilt, pendingFreeTextComm };
    },
    []
  );

  /** Clear the finalised intent + ref together. */
  const clearFinalResult = useCallback(() => {
    setBuilt(null);
    lastBuildResultRef.current = null;
  }, []);

  // Build available commands (static + dynamic spells + abilities), then
  // pre-flight each one's completion steps. Commands whose static-context
  // steps would have 0 options (empty inventory → /give /drop /equip
  // hidden; solo player → /give hidden because step 2 `party_members` is
  // empty; no scene items → /pickup hidden; etc.) are dropped from the
  // menu so the player never opens a picker against an empty pool.
  // Context-dependent steps (item_spells, item_abilities, /use's
  // conditional friendly_targets) fall through to the runtime chat-bubble
  // fallback.
  const availableCommands = useMemo((): GameSlashCommand[] => {
    const mode = ctx.inCombat ? 'combat' : 'exploration';
    const staticCmds = GAME_COMMANDS.filter((cmd) => {
      // Hide /end-conc when nothing is concentrating — the BE handler is a
      // silent no-op for that case (no character_change, no log entry), so
      // exposing the command in the menu would let the player fire an
      // intent that produces no narrative beat and looks like the slash
      // failed silently.
      if (cmd.name === 'end-conc' && !ctx.playerConcentration) return false;
      // /font-of-inspiration (Bard L5+) — mirror the backend's authoritative
      // gate: primary signal is `class_features` containing "Font of
      // Inspiration" (multiclass-aware), fallback is `class === 'bard' &&
      // level >= 5` when features are unhydrated. Backend handler still
      // gates on the same signal, so the FE just hides the slash for users
      // who can't fire it — backend remains authoritative.
      if (cmd.name === 'font-of-inspiration') {
        const hasFoIFeature = (ctx.playerClassFeatures ?? []).some(
          (f) => f.name.toLowerCase() === 'font of inspiration'
        );
        if (hasFoIFeature) return true;
        // Fallback only when feature list is unhydrated (length 0).
        // Lowercase the class string before comparing — production data
        // stores `"Bard"` (title case), the tests previously used
        // `"bard"`, so the literal-lowercase match passed in tests but
        // would silently fail in prod for Bards whose `class_features`
        // hadn't been hydrated yet. Mobile already does this.
        if ((ctx.playerClassFeatures ?? []).length === 0) {
          if (ctx.playerClass?.toLowerCase() === 'bard' && (ctx.playerLevel ?? 0) >= 5) {
            return true;
          }
        }
        return false;
      }
      if (!cmd.gameModes) return true;
      return cmd.gameModes.includes(mode);
    });
    const spellCmds = getSpellCommands(
      ctx.spellcasting,
      ctx.spellSlotsUsed,
      ctx.spellMechanics,
      ctx.preparedSpells,
      ctx.freeCastContext,
      ctx.playerConcentration
    );
    const abilityCmds = getAbilityCommands(
      ctx.actionCards,
      ctx.cardPoolRemaining,
      ctx.spentCardIds
    );
    const all = [...staticCmds, ...spellCmds, ...abilityCmds];
    return all.filter((cmd) => commandIsAvailable(cmd, ctx));
    // `ctx` is the single source of every field commandIsAvailable +
    // get*Commands read — list it alone so the exhaustive-deps lint
    // doesn't fire on field-level access. Adding the individual fields
    // is redundant: a `ctx` change is required to change any of them.
  }, [ctx]);

  // Build menu options for a given step type
  const buildOptionsForStep = useCallback(
    (
      stepType: CompletionStepType,
      label?: string,
      commandMeta?: Record<string, unknown>,
      priorSelections?: StepSelection[]
    ): SlashCommandDefinition[] => {
      const stageLabel = label ?? STEP_LABELS[stepType];
      switch (stepType) {
        case 'enemies':
          return buildEnemyOptions(ctx.sceneCharacters, stageLabel);
        case 'allies':
          return buildAllyOptions(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
        case 'allies_or_self':
          return buildAllyOrSelfOptions(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
        case 'dying_allies':
          return buildDyingAllyOptions(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
        case 'dead_allies':
          return buildDeadAllyOptions(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
        case 'all_targets': {
          // Audience-curated target roster. The step type stays `all_targets`
          // (so multi-select / twinned / AoE-label / empty-bubble logic is
          // untouched) but the OPTION SET is swapped per the picked spell's
          // `target_audience`: hostile → enemies, friendly → self+allies+non-
          // hostile NPCs, self_only → just the Self row (auto-commits). Absent
          // audience (legacy spells, abilities, plain items) → full roster, so
          // no regression. Menu-only — typed/free-text casts still resolve any
          // target server-side.
          const pickedSpell = resolvePickedSpellName(priorSelections, commandMeta);
          // Revive spells target a DEAD ally — swap to the dead-only picker
          // before audience curation (a friendly-audience Revivify would
          // otherwise hit the dead-excluding friendly picker). Mirrors
          // getSpellCommands + mobile.
          if (pickedSpell && isReviveSpell(pickedSpell)) {
            return buildDeadAllyOptions(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
          }
          // Primary source: spell_mechanics (covers all known/prepared spells).
          // Fallback: `spellTargetAudience` stamped in `triggerItemShortcut` from the
          // enricher's ChargableSpell.target_audience (covers item spells that appear
          // in the character's spell list). Absent for cross-class item spells →
          // full roster (non-regression). (spell-target follow-up #1)
          const audience =
            (pickedSpell
              ? ctx.spellMechanics?.find((s) => s.name === pickedSpell)?.target_audience
              : undefined) ?? (commandMeta?.spellTargetAudience as string | undefined);
          switch (audience) {
            case 'hostile':
              return buildEnemyOptions(ctx.sceneCharacters, stageLabel);
            case 'friendly':
              return buildFriendlyTargetOptions(
                ctx.partyMembers,
                ctx.sceneCharacters,
                ctx.playerCharacterId,
                stageLabel
              );
            case 'self_only':
              return buildSelfOnlyTargetOption(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
            default:
              return buildAllTargetOptions(
                ctx.partyMembers,
                ctx.sceneCharacters,
                ctx.playerCharacterId,
                stageLabel
              );
          }
        }
        case 'friendly_targets':
          return buildFriendlyTargetOptions(
            ctx.partyMembers,
            ctx.sceneCharacters,
            ctx.playerCharacterId,
            stageLabel
          );
        case 'spells': {
          const base = buildSpellOptions(
            ctx.spellcasting,
            ctx.spellSlotsUsed,
            stageLabel,
            ctx.spellMechanics,
            ctx.preparedSpells,
            ctx.freeCastContext,
            ctx.playerConcentration
          );
          // Transmuted Spell metamagic — restrict the spells step to
          // spells whose effect carries a `damage_type`. Per SRD 5.2 the
          // metamagic swaps the spell's damage type, so non-damaging
          // spells (Mage Hand, Light, Cure Wounds, Charm Person) burn
          // the SP for zero mechanical effect.
          const cardBuilt = commandMeta?.cardBuilt as
            | { intent?: { intent_type?: string; metamagic?: string[] } }
            | undefined;
          const isTransmutedFlow =
            cardBuilt?.intent?.intent_type === 'cast_spell' &&
            cardBuilt?.intent?.metamagic?.includes('transmuted_spell') === true;
          if (!isTransmutedFlow) return base;
          return base.filter((opt) => {
            const spellName = (opt.sourceId ?? opt.name) as string;
            const mech = ctx.spellMechanics?.find((s) => s.name === spellName);
            const effect = mech?.effect as { damage_type?: string } | undefined;
            return !!effect?.damage_type;
          });
        }
        case 'inventory_items':
          // /give passes `excludeQuestItems: true` so quest-critical items
          // (Amulet of Theron, etc.) don't appear in the recipient picker.
          // /drop passes `excludeWondrous: true` so wondrous magic items
          // never list as droppable. /equip sees everything.
          return buildInventoryItemOptions(ctx.inventory ?? ctx.equipment, stageLabel, {
            excludeQuestItems: commandMeta?.excludeQuestItems === true,
            excludeWondrous: commandMeta?.excludeWondrous === true,
          });
        case 'usable_items':
          return buildUsableItemOptions(ctx.inventory ?? ctx.equipment, stageLabel);
        case 'equippable_items':
          return buildEquippableItemOptions(ctx.inventory ?? ctx.equipment, stageLabel);
        case 'scene_items':
          return buildSceneItemOptions(ctx.sceneItems, stageLabel);
        case 'scene_objects':
          return buildSceneObjectOptions(ctx.sceneInteractables, stageLabel);
        case 'openable_objects':
          return buildOpenableObjectOptions(ctx.sceneInteractables, stageLabel);
        case 'closable_objects':
          return buildClosableObjectOptions(ctx.sceneInteractables, stageLabel);
        case 'breakable_objects':
          return buildBreakableObjectOptions(ctx.sceneInteractables, stageLabel);
        case 'scene_chars':
          return buildSceneCharOptions(
            ctx.partyMembers,
            ctx.sceneCharacters,
            ctx.playerCharacterId,
            stageLabel
          );
        case 'scene_npcs':
          return buildSceneNpcOptions(ctx.sceneCharacters, stageLabel);
        case 'party_members':
          return buildPartyMemberOptions(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
        case 'item_spells': {
          const spells =
            (commandMeta?.itemSpells as Array<{ spellName: string; charges: number }>) ?? [];
          const charges = (commandMeta?.itemCharges as number) ?? 0;
          return buildItemSpellOptions(spells, charges, stageLabel);
        }
        case 'item_abilities': {
          const abilities =
            (commandMeta?.itemAbilities as Array<{
              name: string;
              trigger: string;
              charges?: number;
            }>) ?? [];
          return buildItemAbilityOptions(abilities, stageLabel);
        }
        case 'travel_destinations':
          return buildTravelDestinationOptions(ctx.travelDestinations, stageLabel);
        case 'pool_spend': {
          // Paladin Lay on Hands amount picker. `commandMeta.poolRemaining`
          // is populated by `triggerCardShortcut` when it builds the
          // synthetic command for the LoH click.
          const remaining = (commandMeta?.poolRemaining as number) ?? 0;
          return buildPoolSpendOptions(remaining, stageLabel);
        }
        case 'spell_slot_levels':
          // Bard L5+ Font of Inspiration spell-slot picker. Lists each
          // level the caster has at least one remaining slot for.
          return buildSpellSlotLevelOptions(
            ctx.spellcasting?.max_spell_slots,
            ctx.spellSlotsUsed,
            stageLabel
          );
        case 'transmuted_damage_type': {
          // Sorcerer Transmuted Spell metamagic — six fixed SRD-allowed
          // damage types (Acid, Cold, Fire, Lightning, Poison, Thunder).
          // Step runs AFTER the spell pick so we can read the spell's
          // natural damage_type from priorSelections and filter the
          // no-op (Fire→Fire, etc.). Mirrors the twinned_target pattern.
          let lastSpell: StepSelection | undefined;
          if (priorSelections) {
            for (let i = priorSelections.length - 1; i >= 0; i--) {
              const sel = priorSelections[i];
              if (sel.stepType === 'spells' || sel.stepType === 'item_spells') {
                lastSpell = sel;
                break;
              }
            }
          }
          let excludeType: DamageType | undefined;
          if (lastSpell) {
            const spellName = (lastSpell.selectedItem.sourceId ??
              lastSpell.selectedItem.name) as string;
            const spellMech = ctx.spellMechanics?.find((s) => s.name === spellName);
            // Spell effects carry damage_type via `attack_roll.damage_type`
            // or `saving_throw.damage_type`. Both shapes are tagged enums
            // so probe defensively — non-damaging spells leave it
            // undefined and the picker falls through with all 6 options.
            const effect = spellMech?.effect as { damage_type?: DamageType } | undefined;
            excludeType = effect?.damage_type;
          }
          return buildTransmutedDamageTypeOptions(stageLabel, excludeType);
        }
        case 'twinned_target': {
          // Sorcerer Twinned Spell — list valid second targets (same
          // roster as the spell's first-target step, minus the picked
          // primary). When the picked spell isn't twinnable, return []
          // so the empty-state bubble "Can't twin this spell." fires.
          let lastSpell: StepSelection | undefined;
          let firstTargetSel: StepSelection | undefined;
          if (priorSelections) {
            for (const sel of priorSelections) {
              if (sel.stepType === 'spells' || sel.stepType === 'item_spells') {
                lastSpell = sel;
              } else if (
                sel.stepType === 'all_targets' ||
                sel.stepType === 'enemies' ||
                sel.stepType === 'allies' ||
                sel.stepType === 'allies_or_self' ||
                sel.stepType === 'friendly_targets'
              ) {
                firstTargetSel = sel;
              }
            }
          }
          if (!lastSpell) return [];
          const spellName = (lastSpell.selectedItem.sourceId ??
            lastSpell.selectedItem.name) as string;
          const spellMech = ctx.spellMechanics?.find((s) => s.name === spellName);
          if (!spellMech?.twinnable) return [];
          const primaryId = firstTargetSel?.selectedItem.sourceId ?? null;
          // Second target follows the SAME audience curation as the primary —
          // a twinned single-target buff (Haste) offers allies, a twinned
          // attack cantrip (Fire Bolt) offers enemies. self_only spells can't
          // be twinned (SRD requires distinct targets), so they fall through
          // to the full roster harmlessly; the empty-state guard above already
          // bailed for non-twinnable spells.
          let targets: SlashCommandDefinition[];
          switch (spellMech.target_audience) {
            case 'hostile':
              targets = buildEnemyOptions(ctx.sceneCharacters, stageLabel);
              break;
            case 'friendly':
              targets = buildFriendlyTargetOptions(
                ctx.partyMembers,
                ctx.sceneCharacters,
                ctx.playerCharacterId,
                stageLabel
              );
              break;
            default:
              targets = buildAllTargetOptions(
                ctx.partyMembers,
                ctx.sceneCharacters,
                ctx.playerCharacterId,
                stageLabel
              );
          }
          return targets.filter((o) => o.sourceId !== primaryId);
        }
        case 'careful_exclude': {
          // Sorcerer Careful Spell — multi-select exclude list of party
          // allies caught in the spell's AoE. v1 lists every party ally
          // (engine validator caps to `min(CHA mod, allies in radius)`);
          // the multi-select sheet handles the cap UX. When no allies
          // exist, return [] so the auto-pick path skips the step.
          return buildAllyOrSelfOptions(ctx.partyMembers, ctx.playerCharacterId, stageLabel);
        }
        case 'bestow_curse_effect': {
          // SRD 5.2 Bestow Curse — caster picks one of four effects on
          // cast. AbilityDisadvantage continues to `ability_picker` for
          // the sub-pick.
          const items: SlashCommandDefinition[] = [
            {
              name: 'Disadvantage on one ability',
              description: 'Target rolls checks + saves with the chosen ability at disadvantage.',
              sourceId: 'AbilityDisadvantage',
              meta: { bestowCurseVariant: 'AbilityDisadvantage' },
              isMenuOption: true,
            },
            {
              name: 'Disadvantage on attacks vs you',
              description: "Target's attack rolls against the caster are made at disadvantage.",
              sourceId: 'AttacksVsCasterDisadvantage',
              meta: { bestowCurseVariant: 'AttacksVsCasterDisadvantage' },
              isMenuOption: true,
            },
            {
              name: 'WIS save or forced Dodge',
              description:
                'At start of turn target makes a WIS save or is forced to take the Dodge action.',
              sourceId: 'ForceDodgeOnFailedWis',
              meta: { bestowCurseVariant: 'ForceDodgeOnFailedWis' },
              isMenuOption: true,
            },
            {
              name: 'Your hits deal +1d8 Necrotic',
              description:
                "Caster's attack rolls and spells against target deal an extra 1d8 Necrotic damage.",
              sourceId: 'CasterDealsExtraNecrotic',
              meta: { bestowCurseVariant: 'CasterDealsExtraNecrotic' },
              isMenuOption: true,
            },
          ];
          return items;
        }
        case 'ability_picker': {
          // Six fixed ability options — source-id matches the canonical
          // lower-case key (strength, dexterity, …) the backend Ability
          // enum deserializes from.
          return [
            { name: 'Strength', description: 'STR', sourceId: 'strength', isMenuOption: true },
            { name: 'Dexterity', description: 'DEX', sourceId: 'dexterity', isMenuOption: true },
            {
              name: 'Constitution',
              description: 'CON',
              sourceId: 'constitution',
              isMenuOption: true,
            },
            {
              name: 'Intelligence',
              description: 'INT',
              sourceId: 'intelligence',
              isMenuOption: true,
            },
            { name: 'Wisdom', description: 'WIS', sourceId: 'wisdom', isMenuOption: true },
            { name: 'Charisma', description: 'CHA', sourceId: 'charisma', isMenuOption: true },
          ];
        }
        case 'slot_level_choice': {
          // Upcast picker between `spells` and `all_targets`. Pull the
          // picked spell's base level + flavor from the prior selection.
          // Per-spell dynamic commands (e.g. `/cure-wounds`) skip the
          // `spells` step but stash `spellLevel` / `isCantrip` directly
          // on `command.meta` — fall back to that when no prior
          // selection is present (e.g. Ctrl+N shortcut entering at
          // stepIndex 1 with a seeded selection).
          let lastSpell: StepSelection | undefined;
          if (priorSelections) {
            for (let i = priorSelections.length - 1; i >= 0; i--) {
              const sel = priorSelections[i];
              if (sel.stepType === 'spells' || sel.stepType === 'item_spells') {
                lastSpell = sel;
                break;
              }
            }
          }
          const metaLvl = lastSpell?.selectedItem.meta?.spellLevel as number | undefined;
          const cmdLvl = commandMeta?.spellLevel as number | undefined;
          const baseLevel = metaLvl ?? cmdLvl ?? 0;
          const isCantrip = baseLevel === 0;
          const lastFree = lastSpell?.selectedItem.meta?.freeCast === true;
          const cmdFree = (commandMeta?.freeCast as boolean | undefined) === true;
          const isFreeCast = lastFree || cmdFree;
          return buildSlotLevelChoiceOptions(
            baseLevel,
            isCantrip,
            isFreeCast,
            ctx.spellcasting?.max_spell_slots,
            ctx.spellSlotsUsed,
            stageLabel
          );
        }
        case 'concentration_confirm': {
          // Two-option swap confirm. Source of truth, in priority order:
          //   1. Prior spell pick's `meta.concentrationToReplace` —
          //      `buildSpellOptions` tags this when a swap applies on the
          //      `/cast spells` flow.
          //   2. Command-level `commandMeta.concentrationToReplace` /
          //      `commandMeta.spellName` — per-spell slash commands
          //      (`getSpellCommands`) tag this since the flow has no
          //      `spells` step before the confirm.
          //   3. `ctx.playerConcentration` fallback for the current name.
          let lastSpell: StepSelection | undefined;
          if (priorSelections) {
            for (let i = priorSelections.length - 1; i >= 0; i--) {
              const sel = priorSelections[i];
              if (sel.stepType === 'spells' || sel.stepType === 'item_spells') {
                lastSpell = sel;
                break;
              }
            }
          }
          const cmdConc = commandMeta?.concentrationToReplace as string | undefined;
          const cmdSpell = commandMeta?.spellName as string | undefined;
          const currentConc =
            (lastSpell?.selectedItem.meta?.concentrationToReplace as string | undefined) ??
            cmdConc ??
            ctx.playerConcentration ??
            'current spell';
          const pickedSpell = lastSpell?.selectedItem.name ?? cmdSpell ?? 'this spell';
          return [
            {
              name: `Continue — end ${currentConc}`,
              description: `Casting ${pickedSpell} replaces your concentration on ${currentConc}.`,
              sourceId: '__conc_continue__',
              meta: { concentrationConfirm: true },
              isMenuOption: true,
            },
            {
              name: 'Cancel',
              description: 'Go back to spell selection.',
              sourceId: '__conc_cancel__',
              meta: { cancelToSpellPick: true },
              isMenuOption: true,
            },
          ];
        }
      }
    },
    [ctx]
  );

  // Track recent commands from localStorage
  const [recentNames, setRecentNames] = useState<string[]>(getRecentCommands);

  // Refresh recent on mount
  useEffect(() => {
    setRecentNames(getRecentCommands());
  }, []);

  // Alphabetized commands with recent ones promoted to top.
  // `travel` and `end-session` are pinned to the bottom (in that order)
  // so the most consequential exits stay easy to find — both got buried
  // mid-alphabetical-list otherwise.
  const alphabetizedCommands = useMemo(() => {
    const cmds = [...availableCommands] as SlashCommandDefinition[];
    cmds.sort((a, b) => a.name.localeCompare(b.name));

    const bottomPins = ['travel', 'end-session'];
    const pinned: SlashCommandDefinition[] = [];
    for (const name of bottomPins) {
      const idx = cmds.findIndex((c) => c.name === name);
      if (idx >= 0) pinned.push(cmds.splice(idx, 1)[0]);
    }

    if (recentNames.length === 0) return [...cmds, ...pinned];

    const recentSet = new Set(recentNames);
    const recent: SlashCommandDefinition[] = [];
    const rest: SlashCommandDefinition[] = [];
    for (const cmd of cmds) {
      if (recentSet.has(cmd.name)) {
        recent.push(cmd);
      } else {
        rest.push(cmd);
      }
    }
    // Sort recent by recency order (most recent first)
    recent.sort((a, b) => recentNames.indexOf(a.name) - recentNames.indexOf(b.name));
    return [...recent, ...rest, ...pinned];
  }, [availableCommands, recentNames]);

  // Compute menu items based on current stage
  const menuItems = useMemo((): SlashCommandDefinition[] => {
    switch (stage.type) {
      case 'idle':
      case 'done':
        return [];
      case 'command_select': {
        if (!stage.filter) return alphabetizedCommands;
        // Fuzzy search with match positions for highlighting
        return fuzzyFilterCommands(alphabetizedCommands, stage.filter);
      }
      case 'option_select': {
        const rawOptions = buildOptionsForStep(
          stage.stepType,
          undefined,
          stage.command.meta,
          stage.selections
        );
        // Apply multi-select layer: mark already-checked options + prepend
        // a synthetic "Confirm" pseudo-item so the user has a discoverable
        // way to finalise the picks. The Confirm row never matches the
        // fuzzy filter (it has no descriptive keywords) — for the typed-text
        // path the user has to hit Esc / back out and use Enter on the
        // highlighted Confirm row from arrow-key nav.
        const decorated = stage.multiSelect
          ? [
              buildMultiSelectConfirmItem(
                stage.multiSelect.selectedItems.length,
                stage.multiSelect.max
              ),
              ...applyMultiSelectMarkers(rawOptions, stage.multiSelect.selectedItems),
            ]
          : rawOptions;
        if (!stage.filter) return decorated;
        // Fuzzy filter sub-menu options too. In multi-select mode we exclude
        // the Confirm pseudo-item from the filter (it always belongs at the
        // top regardless of typed filter) — but the typical user navigates
        // the multi-select stage by clicking / arrow keys, not by typing.
        if (stage.multiSelect) {
          const [confirmItem, ...rest] = decorated;
          return [confirmItem, ...fuzzyFilterCommands(rest, stage.filter)];
        }
        return fuzzyFilterCommands(decorated, stage.filter);
      }
    }
  }, [stage, alphabetizedCommands, buildOptionsForStep]);

  const isActive = stage.type === 'command_select' || stage.type === 'option_select';
  const showMenu = menuItems.length > 0 && isActive;
  // Command-level stage-label override (e.g. point-target cards relabel
  // their enemy picker "Center the AoE on…" so the player understands
  // they're picking a focal point, not a single target). Falls through
  // to the per-step default in `STEP_LABELS` when no override is set.
  // The override only applies to TARGET steps — a metamagic+point card
  // (`spells → transmuted_damage_type → all_targets`) shouldn't relabel
  // its spell pick or damage-type pick as "Center the AoE on…".
  const stageLabel: string | null = (() => {
    if (stage.type !== 'option_select') return null;
    const override = stage.command.meta?.targetStageLabel as string | undefined;
    const isTargetStep =
      stage.stepType === 'enemies' ||
      stage.stepType === 'all_targets' ||
      stage.stepType === 'allies' ||
      stage.stepType === 'allies_or_self' ||
      stage.stepType === 'friendly_targets';
    if (override && isTargetStep) return override;
    // AoE label parity for `/cast`: a hostile AoE spell (Fireball) reaches the
    // target picker via `all_targets` → enemies substitution. The card path
    // labels point-target AoE spells "Center the AoE on…" via
    // `meta.targetStageLabel`; mirror that here for the typed/cast flow so the
    // player knows they're picking a focal point, not a single victim.
    if (stage.stepType === 'all_targets') {
      const pickedSpell = resolvePickedSpellName(stage.selections, stage.command.meta);
      const mech = pickedSpell
        ? ctx.spellMechanics?.find((s) => s.name === pickedSpell)
        : undefined;
      const isAoe = !!(mech?.effect as { area?: unknown } | undefined)?.area;
      if (mech?.target_audience === 'hostile' && isAoe) return 'Center the AoE on…';
    }
    return STEP_LABELS[stage.stepType] ?? null;
  })();
  const isMultiStep = stage.type === 'option_select';

  // Build the final text + typed Intent envelope from a completed multi-step
  // flow. Thin wrapper around `buildIntent` so the menu state and the Intent
  // constructor share one definition.
  const buildResult = useCallback(
    (
      command: GameSlashCommand,
      selections: StepSelection[]
    ): { text: string; built: BuiltIntent | null } => buildIntent(command, selections),
    []
  );

  /**
   * Drive the picker from step `stepIndex` of `command`. Single
   * gate for the 0 / 1 / 2+ option invariant — every entry point
   * (command pick, auto-advance, programmatic shortcut, multi-select
   * confirm) routes through here.
   *
   *  - **0 options at step**: dispatch a local italic chat bubble (via
   *    `gameStore.addLocalBubble`) using `EMPTY_STATE_BUBBLE[step]`, reset
   *    the machine to idle, and return `noop`.
   *  - **1 option**: auto-pick the lone option, advance to the next step
   *    (honoring `shouldSkipNextStep`), and loop. If every step auto-picks,
   *    the command finalizes immediately without ever opening a sheet.
   *  - **2+ options**: set stage to `option_select` and return `next_step`.
   *  - **past the last step**: finalize via `buildResult` and set stage to
   *    `done`.
   */
  const advanceToStep = useCallback(
    (
      command: GameSlashCommand,
      initialStepIndex: number,
      initialSelections: StepSelection[]
    ): SlashSelectResult => {
      const steps = command.completionSteps ?? [];
      let stepIndex = initialStepIndex;
      let selections = initialSelections;

      while (stepIndex < steps.length) {
        const stepType = steps[stepIndex];
        const options = buildOptionsForStep(stepType, undefined, command.meta, selections);

        if (options.length === 0) {
          useGameStore
            .getState()
            .addLocalBubble(EMPTY_STATE_BUBBLE[stepType], ctx.playerCharacterId);
          setStage({ type: 'idle' });
          clearFinalResult();
          baseTextRef.current = '';
          setSelectedIndex(0);
          return { action: 'noop' };
        }

        const multiMax = computeMultiSelectMaxForStep(
          stepType,
          selections,
          ctx.spellMechanics,
          ctx.partyMembers.length
        );

        // Auto-pick when there's exactly one option — but ONLY for the small
        // set of mechanical/shortcut steps (attack target, slot level). Every
        // other step is a player-facing choice picker that must open even with
        // one option, so a stray submit can't silently commit a consequential
        // action like travelling out of an encounter (#345). Exception:
        // `all_targets` curated to `self_only` audience (buildSelfOnlyTargetOption)
        // is structurally always exactly the caster, never a circumstantial
        // 1-option roster: leaving the picker open there just costs the
        // player a silently-discarded turn if they don't click through.
        if (
          options.length === 1 &&
          (AUTO_PICK_SINGLE_OPTION_STEPS.has(stepType) ||
            options[0].meta?.audienceSelfOnly === true)
        ) {
          const pickedItem = options[0];
          const newSelections: StepSelection[] = [
            ...selections,
            {
              stepType,
              selectedItem: pickedItem,
              ...(multiMax !== null ? { selectedItems: [pickedItem] } : {}),
            },
          ];
          // Walk forward through ALL skippable steps (not just one) so
          // chains like bestow_curse_effect → ability_picker get fully
          // skipped for non-Bestow spells. Single-skip worked when /cast
          // had only one conditional step; breaks with two consecutive.
          let nextIdx = stepIndex + 1;
          while (nextIdx < steps.length) {
            const candidate = steps[nextIdx];
            if (shouldSkipNextStep(command, candidate, pickedItem)) {
              nextIdx += 1;
            } else {
              break;
            }
          }
          stepIndex = nextIdx;
          selections = newSelections;
          continue;
        }

        // 2+ options or multi-select step — open the picker.
        const baseText =
          stepIndex === 0 ? getBaseTextForStep(command, 0) : buildPartialText(command, selections);
        baseTextRef.current = baseText;
        setStage({
          type: 'option_select',
          command,
          stepIndex,
          stepType,
          baseText,
          filter: '',
          selections,
          multiSelect: multiMax !== null ? { max: multiMax, selectedItems: [] } : undefined,
        });
        setSelectedIndex(0);
        return { action: 'next_step', baseText };
      }

      // Past all steps — finalize.
      const { text, built: builtIntent } = buildResult(command, selections);
      commitFinalResult(text, builtIntent, pendingFreeTextCommFromMenu(command, selections));
      setStage({ type: 'done' });
      return {
        action: 'fill',
        text,
        built: builtIntent,
        intent: builtIntent?.intent ?? null,
        done: !command.endsWithFreeText,
      };
    },
    [
      buildOptionsForStep,
      buildResult,
      clearFinalResult,
      commitFinalResult,
      ctx.playerCharacterId,
      ctx.spellMechanics,
      ctx.partyMembers.length,
    ]
  );

  // Handle text changes from the input
  const handleTextChange = useCallback(
    (text: string): SlashSelectResult => {
      // option_select: user is editing inside the filled template — either filter
      // the current step or delete back into the base text to go back a step.
      if (stage.type === 'option_select') {
        const baseText = baseTextRef.current;
        if (text.length < baseText.length) {
          // User deleted into/past the base text — go back one step.
          // Trigger back-nav when text no longer contains the core base text
          // (base text minus its trailing space, so deleting the space triggers back).
          const coreBase = baseText.endsWith(' ') ? baseText.slice(0, -1) : baseText;
          if (!text.startsWith(coreBase)) {
            // Go back to previous stage
            if (stage.stepIndex === 0) {
              setStage({ type: 'command_select', filter: '' });
            } else {
              // Go back to previous step (mirror handleBack logic)
              const prevStepIndex = stage.stepIndex - 1;
              const prevSelections = stage.selections.slice(0, -1);
              const prevStep = stage.command.completionSteps![prevStepIndex];
              const prevBaseText =
                prevStepIndex === 0
                  ? getBaseTextForStep(stage.command, 0)
                  : buildPartialText(stage.command, prevSelections);
              baseTextRef.current = prevBaseText;
              setStage({
                type: 'option_select',
                command: stage.command,
                stepIndex: prevStepIndex,
                stepType: prevStep,
                baseText: prevBaseText,
                filter: '',
                selections: prevSelections,
              });
            }
            setSelectedIndex(0);
            return { action: 'noop' };
          }
        }
        // Extract filter text after the base text. Strip any leading
        // whitespace so spaces between picker stages don't count as typing
        // (per UX spec: "Use ${ability} on " then auto-advancing into the
        // target picker should not treat the trailing-space-then-input as a
        // filter starting with " "). Without this, fuzzyFilter never matches
        // because no option starts with whitespace.
        const filter = text.slice(baseText.length).replace(/^\s+/, '');
        setStage((prev) => (prev.type === 'option_select' ? { ...prev, filter } : prev));
        setSelectedIndex(0);
        return { action: 'noop' };
      }

      // idle | done | command_select: user is typing in the chat input.
      // If text doesn't start with `/`, return to idle (cleaning up any done-state hint).
      if (!text.startsWith('/')) {
        if (stage.type === 'done') {
          // PRESERVE `lastBuildResultRef` when the user is appending a body
          // to the canonical text of an endsWithFreeText comm menu (`/say`,
          // `/sayto`, `/whisper`, `/shout`, `/emote`). Without this guard the
          // ref is wiped here and `finalizeForSubmit`'s typed-comm rebuilder
          // at the `last.pendingFreeTextComm` branch can never fire — every
          // comm with a typed body falls through to `player_text` and the
          // backend enricher LLM has to re-resolve the recipient name the
          // picker already gave us. Verified 2026-05-31 (scenario run 2).
          const canonical = lastBuildResultRef.current?.canonicalText;
          const extendingCanonical = !!canonical && text.startsWith(canonical);
          if (!extendingCanonical) {
            clearFinalResult();
          }
        }
        if (stage.type !== 'idle') {
          setStage({ type: 'idle' });
        }
        return { action: 'noop' };
      }

      // Entering / in done state — clear the previous hint so a fresh flow starts clean.
      if (stage.type === 'done') {
        clearFinalResult();
      }

      const filter = text.slice(1);
      setStage({ type: 'command_select', filter });
      setSelectedIndex(0);

      // Auto-advance into option_select when the filter exactly matches a
      // multi-step command name (e.g. `/cast` → spell picker, `/travel` →
      // destination picker) and no other command starts with this name as a
      // prefix (so `/say` doesn't prematurely commit while `/sayto` is still
      // reachable, and same for `/give` vs `/givecoins`).
      //
      // Trailing-space carve-out: if the user explicitly typed a space after
      // the complete command (`/give␣`), they've disambiguated even when a
      // longer-prefix candidate exists — `/give␣` clearly means `/give`,
      // not the still-typeable `/givecoins`. Treat the trailing space as the
      // commit signal and auto-advance regardless of `hasLongerCandidate`.
      const hasTrailingSpace = filter.endsWith(' ');
      const trimmedFilter = filter.trimEnd();
      if (!trimmedFilter) return { action: 'noop' };
      const lowerFilter = trimmedFilter.toLowerCase();
      const exactMatch = availableCommands.find((c) => c.name.toLowerCase() === lowerFilter);
      if (!exactMatch?.completionSteps || exactMatch.completionSteps.length === 0) {
        return { action: 'noop' };
      }
      if (!hasTrailingSpace) {
        const hasLongerCandidate = availableCommands.some(
          (c) =>
            c.name.toLowerCase() !== lowerFilter && c.name.toLowerCase().startsWith(lowerFilter)
        );
        if (hasLongerCandidate) return { action: 'noop' };
      }

      saveRecentCommand(exactMatch.name);
      setRecentNames(getRecentCommands());
      return advanceToStep(exactMatch, 0, []);
    },
    [stage, availableCommands, advanceToStep, clearFinalResult]
  );

  // Handle selection of a menu item
  const handleSelect = useCallback(
    (item: SlashCommandDefinition): SlashSelectResult => {
      // Defensive: callers occasionally hit `handleSelect` with a stale or
      // undefined item — e.g., when the picker auto-finalised between
      // `find(...)` and the `act(... handleSelect(found!))` call, leaving
      // `found` undefined. Without this guard the next `item.sourceId`
      // dereference throws and the input freezes mid-test.
      if (!item) return { action: 'noop' };
      // Don't select placeholder items (like "No enemies visible")
      if (item.sourceId == null && item.isMenuOption) {
        return { action: 'noop' };
      }

      if (stage.type === 'command_select') {
        // Find the matching game command
        const cmd = availableCommands.find((c) => c.name === item.name);
        if (!cmd) return { action: 'noop' };

        // Track recently used command
        saveRecentCommand(cmd.name);
        setRecentNames(getRecentCommands());

        // Zero-step command — fill text and done. advanceToStep starting at
        // stepIndex 0 with empty steps[] would finalize anyway, but going
        // direct here saves an iteration and keeps the call shape clear.
        if (!cmd.completionSteps || cmd.completionSteps.length === 0) {
          const { text, built: builtIntent } = buildResult(cmd, []);
          commitFinalResult(text, builtIntent, pendingFreeTextCommFromMenu(cmd, []));
          setStage({ type: 'done' });
          return {
            action: 'fill',
            text,
            built: builtIntent,
            intent: builtIntent?.intent ?? null,
            done: !cmd.endsWithFreeText,
          };
        }

        // Multi-step command — drive into the first step. advanceToStep
        // handles 0/1/2+ options uniformly + cascades auto-picks until
        // it either opens a picker, fires a bubble, or finalizes.
        return advanceToStep(cmd, 0, []);
      }

      if (stage.type === 'option_select') {
        const { command, stepIndex, stepType, selections, multiSelect } = stage;

        // Multi-select branch: toggle target on each pick, finalise on Confirm
        // pseudo-item. Stays on the same stage (no `next_step`) until the
        // user confirms — single-target spells never enter this branch.
        if (multiSelect) {
          // The synthetic Confirm pseudo-item routes the stage to the next
          // step (or completes the command if it was the last step). All
          // currently-checked targets ride along in the StepSelection.
          if (item.isMultiSelectConfirm || item.sourceId === MULTI_SELECT_CONFIRM_ID) {
            if (multiSelect.selectedItems.length === 0) {
              // Empty confirm — keep the picker open, don't crash the command.
              return { action: 'noop' };
            }
            const primaryItem = multiSelect.selectedItems[0];
            const stepSelection: StepSelection = {
              stepType,
              selectedItem: primaryItem,
              selectedItems: [...multiSelect.selectedItems],
            };
            const newSelections = [...selections, stepSelection];
            // Delegate to `advanceToStep` so the next step honors the 0/1/2+
            // option invariant (bubble on empty, auto-pick on single, picker
            // on multi) — same path the command-pick branch uses.
            return advanceToStep(command, stepIndex + 1, newSelections);
          }

          // Toggle in-place. Match by sourceId when present (canonical char id
          // / item id from the picker builder); fall back to name for command
          // items without a sourceId.
          const itemId = item.sourceId ?? item.name;
          const isAlreadyChecked = multiSelect.selectedItems.some(
            (it) => (it.sourceId ?? it.name) === itemId
          );
          let nextSelected: SlashCommandDefinition[];
          if (isAlreadyChecked) {
            nextSelected = multiSelect.selectedItems.filter(
              (it) => (it.sourceId ?? it.name) !== itemId
            );
          } else if (multiSelect.selectedItems.length >= multiSelect.max) {
            // At cap — clicking another option is a no-op rather than
            // silently replacing the last pick (which would surprise the
            // user). The renderer dims options at cap; this is defence.
            return { action: 'noop' };
          } else {
            nextSelected = [...multiSelect.selectedItems, item];
          }
          setStage({
            ...stage,
            multiSelect: { ...multiSelect, selectedItems: nextSelected },
          });
          // Keep the same selectedIndex so the user can rapidly tick adjacent
          // rows; the renderer redraws checkmarks on the in-place list.
          return { action: 'noop' };
        }

        // Pool-spend custom-input intercept: the "Other amount…" row
        // surfaces a `request_pool_input` action so the consumer (web
        // ChatPanel) can render a styled numeric Dialog. The consumer
        // calls `handleSelect` again with a synthesized item carrying
        // `meta.poolSpend = <picked>` on confirm.
        if (item.meta?.isPoolCustomInput === true) {
          const poolMax = (item.meta.poolMax as number | undefined) ?? 0;
          if (poolMax <= 0) return { action: 'noop' };
          return { action: 'request_pool_input', poolMax };
        }

        // Cancel-to-spell-pick: the concentration_confirm step renders a
        // "Cancel" row tagged with `meta.cancelToSpellPick: true`. Treat it
        // like the back button — unwind to the most recent `spells` step so
        // the player can choose a different spell (or abort with Esc).
        //
        // Per-spell shortcuts (`/bless`, action-card click, Ctrl+N) carry
        // `completionSteps: ['concentration_confirm', ...]` — they have NO
        // `spells` step, so `findIndex` returns -1. Treat Cancel as "abort
        // the wizard" (back to idle) in that case so the picker doesn't
        // freeze. Esc already routes there; this mirrors the visible Cancel
        // button to the same semantics.
        if (item.meta?.cancelToSpellPick === true) {
          const spellIdx = selections.findIndex((s) => s.stepType === 'spells');
          if (spellIdx >= 0) {
            const prevSelections = selections.slice(0, spellIdx);
            const spellStepIdx = (command.completionSteps ?? []).indexOf('spells');
            return advanceToStep(command, spellStepIdx, prevSelections);
          }
          // No prior spell pick to return to — per-spell shortcut path.
          // Abort the wizard back to idle (mirrors `handleBack` for
          // stepIndex 0). Without this, the visible Cancel button is a
          // no-op and the picker is stuck until the user presses Esc.
          setStage({ type: 'idle' });
          setSelectedIndex(0);
          clearFinalResult();
          baseTextRef.current = '';
          return { action: 'clear' };
        }

        const newSelections = [...selections, { stepType, selectedItem: item }];
        const steps = command.completionSteps ?? [];
        // Delegate next-step transition (or finalize) to advanceToStep so
        // the 0/1/2+ invariant applies uniformly. Walk forward through
        // ALL skippable steps (not just one) so chains like
        // bestow_curse_effect → ability_picker get fully skipped for
        // non-Bestow spells. The single-skip was correct when /cast had
        // only one conditional step but breaks now with two.
        let startAt = stepIndex + 1;
        while (startAt < steps.length) {
          const candidate = steps[startAt];
          if (shouldSkipNextStep(command, candidate, item)) {
            startAt += 1;
          } else {
            break;
          }
        }
        return advanceToStep(command, startAt, newSelections);
      }

      return { action: 'noop' };
    },
    [stage, availableCommands, advanceToStep, buildResult, commitFinalResult, clearFinalResult]
  );

  // Handle Esc — go back one step
  const handleBack = useCallback((): SlashBackResult => {
    if (stage.type === 'command_select') {
      setStage({ type: 'idle' });
      return { action: 'clear' };
    }

    if (stage.type === 'option_select') {
      if (stage.stepIndex === 0) {
        // Go back to command select
        setStage({ type: 'command_select', filter: '' });
        setSelectedIndex(0);
        return { action: 'back_to_commands', text: '/' };
      }
      // Drop the selection for the step we're leaving (only the visible
      // steps have selections; skipped steps never added one). Then find
      // the previous *visible* step by replaying forward navigation from
      // step 0 with the truncated selections, exactly the way the wizard
      // first reached this position. Otherwise Esc can land on a step
      // forward-nav had skipped (e.g. concentration_confirm when the
      // picked spell doesn't trigger a swap).
      const prevSelections = stage.selections.slice(0, -1);
      const cmdSteps = stage.command.completionSteps!;
      let prevStepIndex = 0;
      let visited = 0;
      let found = false;
      for (let i = 0; i < cmdSteps.length; i++) {
        if (i > 0) {
          // Walk past steps the skip predicate would drop, using the
          // selection that feeds the predicate. Skipped steps don't add
          // their own selection, so the feeding entry is the LAST one in
          // prevSelections we've seen so far.
          const feedingSel = prevSelections[visited - 1];
          if (
            feedingSel != null &&
            shouldSkipNextStep(stage.command, cmdSteps[i], feedingSel.selectedItem)
          ) {
            continue;
          }
        }
        if (visited === prevSelections.length) {
          prevStepIndex = i;
          found = true;
          break;
        }
        visited += 1;
      }
      // Defensive fallback — if the replay never landed on a visible step
      // (would imply every remaining step skips under the truncated
      // selections), fall back to the previous selection's catalog index
      // so the user lands on a real step instead of silently rewinding
      // to step 0. With current step schemas this is unreachable; the
      // guard exists so a future conditional-step refactor can't
      // silently misbehave.
      if (!found && prevSelections.length > 0) {
        prevStepIndex = Math.max(0, cmdSteps.length - 1);
      }
      const prevStep = cmdSteps[prevStepIndex];
      const baseText =
        prevStepIndex === 0
          ? getBaseTextForStep(stage.command, 0)
          : buildPartialText(stage.command, prevSelections);
      baseTextRef.current = baseText;
      setStage({
        type: 'option_select',
        command: stage.command,
        stepIndex: prevStepIndex,
        stepType: prevStep,
        baseText,
        filter: '',
        selections: prevSelections,
      });
      setSelectedIndex(0);
      return { action: 'back_to_step', text: baseText };
    }

    if (stage.type === 'done') {
      // User pressed Esc after completing a command — reset state but preserve text
      // (text is already in the input, user can edit or send it)
      setStage({ type: 'idle' });
      clearFinalResult();
      return { action: 'noop' };
    }

    return { action: 'noop' };
  }, [stage, clearFinalResult]);

  const reset = useCallback(() => {
    setStage({ type: 'idle' });
    setSelectedIndex(0);
    clearFinalResult();
    baseTextRef.current = '';
  }, [clearFinalResult]);

  // Submit-time resolver — see GameSlashCommandState.resolveFromSubmit for docs.
  // Two paths: (A) user is mid option_select with a filter typed in — match
  // filter against current-step options and buildResult if that was the final
  // step; (B) raw `/<cmd> <args>` text that never advanced the state machine
  // (paste, fast typing) — parse command + match remainder against the single
  // step's options. Multi-step commands typed raw aren't resolved here; the
  // user can complete them through the menu.
  const resolveFromSubmit = useCallback(
    (text: string): { text: string; built: BuiltIntent | null; intent: Intent | null } | null => {
      const wrapResolved = (
        resolved: { text: string; built: BuiltIntent | null } | null
      ): { text: string; built: BuiltIntent | null; intent: Intent | null } | null =>
        resolved ? { ...resolved, intent: resolved.built?.intent ?? null } : null;
      // Case A: mid-step with filter text
      if (stage.type === 'option_select') {
        const filter = stage.filter.trim();
        if (!filter) return null;
        // `/pickup` menu flow with "all"/"everything" filter — emit the
        // `__all__` sentinel just like Case B (raw `/pickup all`). Without
        // this, a user who typed `/pickup` (auto-advances to scene-items
        // step) and then typed "all" hoping for all-pickup would hit
        // findUnambiguousOptionMatch (which fails since no item is named
        // "all") and the slash falls through as plain text. The sentinel
        // path is the player-intent path; both menu and raw should reach it.
        if (stage.command.name === 'pickup' && isPickupAllPhrase(filter)) {
          return wrapResolved({
            text: 'Pick up everything',
            built: {
              intent: {
                intent_type: 'pick_up',
                item_id: '',
                item_name: PICKUP_ALL_SENTINEL,
              },
              action_slot: 'action',
            },
          });
        }
        const options = buildOptionsForStep(
          stage.stepType,
          undefined,
          stage.command.meta,
          stage.selections
        );
        const match = findUnambiguousOptionMatch(options, filter);
        if (!match) return null;
        const steps = stage.command.completionSteps ?? [];
        // Walk forward through skipped steps to find the next REAL step.
        // /cast now has multiple conditional steps (bestow_curse_effect,
        // ability_picker) that skip for non-Bestow spells; treating
        // shouldSkipNextStep=true as "selection is final" was wrong because
        // slot_level_choice + all_targets still remained. The loop chases
        // the skip chain until either we run out of steps (final) OR we
        // land on a non-skipped step (still navigating).
        let nextRealStepIndex = stage.stepIndex + 1;
        while (nextRealStepIndex < steps.length) {
          const candidate = steps[nextRealStepIndex];
          if (shouldSkipNextStep(stage.command, candidate, match)) {
            nextRealStepIndex += 1;
          } else {
            break;
          }
        }
        const nextStep: CompletionStepType | undefined = steps[nextRealStepIndex];
        // Treat as final if no real next step remains, OR this is `/use`
        // (direct-typing finalize per resolveFromSubmit Case B's policy).
        const isFinalSelection = !nextStep || stage.command.name === 'use';
        if (!isFinalSelection) return null;
        const newSelections = [
          ...stage.selections,
          { stepType: stage.stepType, selectedItem: match },
        ];
        return wrapResolved(buildResult(stage.command, newSelections));
      }

      // Case B: raw `/cmd args` text
      if (!text.startsWith('/')) return null;
      const stripped = text.slice(1).trimStart();
      if (!stripped) return null;
      const spaceIdx = stripped.indexOf(' ');
      const cmdName = (spaceIdx === -1 ? stripped : stripped.slice(0, spaceIdx)).toLowerCase();
      const rawRemainder = spaceIdx === -1 ? '' : stripped.slice(spaceIdx + 1).trim();
      const command = availableCommands.find((c) => c.name.toLowerCase() === cmdName);
      if (!command) return null;

      // Strip the literal text between the cmd verb and the first
      // `{placeholder}` in the textTemplate so natural phrasings like
      // `/travel to village_square` or `/break the door` resolve. No-op
      // when the template has no prefix (`/give`, `/cast`, `/use`, `/help`).
      const prefixLiteral = command.textTemplate
        ? parseTemplatePrefixLiteral(command.textTemplate, command.name)
        : '';
      const remainder =
        prefixLiteral && rawRemainder.toLowerCase().startsWith(prefixLiteral.toLowerCase())
          ? rawRemainder.slice(prefixLiteral.length).trim()
          : rawRemainder;

      // `/pickup all` / `/pickup everything` — emit the `__all__` sentinel that
      // the backend handler expands into one `pick_up` per lootable scene
      // item. Bypasses scene-item match so the player doesn't need to know
      // there's no item literally named "all".
      if (cmdName === 'pickup' && isPickupAllPhrase(remainder)) {
        return wrapResolved({
          text: 'Pick up everything',
          built: {
            intent: {
              intent_type: 'pick_up',
              item_id: '',
              item_name: PICKUP_ALL_SENTINEL,
            },
            action_slot: 'action',
          },
        });
      }

      // Multi-mode mode-keyword override — `/patient-defense
      // focused`, `/step-of-the-wind focused`, `/cunning-action disengage`,
      // etc. Without this the web slash path hard-defaults to the
      // builder's first mode (see `build-card-command.ts` ~L138 / mode
      // ordering in `backend/.../action_cards/builders.rs`), forcing the
      // player into the cheapest variant. The override rebuilds the
      // cardBuilt intent with the requested `mode_id` if it appears on
      // `meta.modeIds` (set by the builder when the card has >1 mode);
      // unknown keywords fall through to the existing remainder handling.
      //
      // `fillText` is also rewritten so the chat bubble shows the picked
      // mode (e.g. `Use Patient Defense (focused)`) instead of the
      // builder-default `(free)`. buildFillText for `use_ability` cards
      // appends the mode_id as a trailing `(modeId)` suffix
      // (build-from-card.ts buildFillText), and use_ability card labels
      // never include parens — the suffix is safe to swap.
      {
        const modeIds = command.meta?.modeIds as readonly string[] | undefined;
        if (modeIds && remainder.length > 0) {
          const requested = remainder.toLowerCase();
          const matched = modeIds.find((m) => m.toLowerCase() === requested);
          if (matched) {
            const original = command.meta?.cardBuilt as BuiltIntent | undefined;
            if (
              original &&
              original.intent.intent_type === 'use_ability' &&
              'mode_id' in original.intent
            ) {
              const overridden: GameSlashCommand = {
                ...command,
                fillText: command.fillText?.replace(/\s*\([^)]*\)\s*$/, ` (${matched})`),
                meta: {
                  ...command.meta,
                  cardBuilt: {
                    ...original,
                    intent: { ...original.intent, mode_id: matched },
                  },
                },
              };
              return wrapResolved(buildResult(overridden, []));
            }
          }
        }
      }

      const steps = command.completionSteps ?? [];

      // Zero-step typed communication: `/say hello`, `/shout hello`. The
      // slash + menu design contract is "fully-formed typed intents with
      // resolved IDs". For these the body becomes the message directly via
      // `buildCommunicationIntent` — no LLM round-trip needed for parsing.
      // (Prior code dropped `built: null` and routed through `player_text`.)
      if (steps.length === 0) {
        const base = buildResult(command, []);
        if (remainder) {
          if (command.endsWithFreeText && command.intentType) {
            const intent = buildCommunicationIntent(command.intentType, remainder);
            if (intent) {
              return wrapResolved({
                text: `${base.text}${remainder}`,
                built: { intent, action_slot: 'action' },
              });
            }
          }
          // Non-communication zero-step or builder returned null — fall back
          // to the legacy text+null contract so the LLM agent handles it.
          return wrapResolved({ text: `${base.text}${remainder}`, built: null });
        }
        return wrapResolved(base);
      }

      if (!remainder) return null;

      // Typed multi-step path: `/give Dagger to bram`, `/cast Fireball at
      // Goblin`, `/use Healing Potion on bram`. Parse the textTemplate's
      // inter-placeholder literals (" to ", " at ", " on ") to split
      // `remainder` into per-step args, then resolve each step's option
      // independently. Skipped when:
      //  - command has 0 or 1 step (single-match path below handles those)
      //  - command has no textTemplate to derive separators from
      //  - separators present but `remainder` doesn't contain them (e.g.
      //    `/give Dagger` with no target) — fall through to menu flow
      //  - any per-step segment fails to match an option
      //
      // Note: `/use <item> on <scene object>` does NOT resolve here. `/use`'s
      // second step is `friendly_targets` (party/scene characters only), so
      // typed `on cave_entrance` against a scene object fails the per-step
      // match and falls through to the menu. Scene-object targeting still
      // needs the menu flow or a dedicated `/interact` command.
      const separators = command.textTemplate ? parseTemplateSeparators(command.textTemplate) : [];
      if (steps.length > 1 && separators.length === steps.length - 1) {
        const segments = splitMultiStepRemainder(remainder, separators);
        if (segments && segments.length === steps.length) {
          const selections: Array<{
            stepType: CompletionStepType;
            selectedItem: SlashCommandDefinition;
          }> = [];
          let allResolved = true;
          for (let i = 0; i < steps.length; i++) {
            const opts = buildOptionsForStep(steps[i], undefined, command.meta);
            const m = findUnambiguousOptionMatch(opts, segments[i]);
            if (!m) {
              allResolved = false;
              break;
            }
            selections.push({ stepType: steps[i], selectedItem: m });
          }
          if (allResolved) {
            return wrapResolved(buildResult(command, selections));
          }
        }
      }

      // Single-step + endsWithFreeText typed body: `/sayto bram hello`,
      // `/whisper bram hello`, `/givecoins bram 5 gp`. Match a token
      // prefix of `remainder` against step-0 options (longest
      // unambiguous wins) and treat the trailing tokens as the message
      // body (or, for `/givecoins`, the amount). Produces a fully-formed
      // typed intent with the resolved target char_id, bypassing the
      // LLM. Falls through to null when no prefix matches.
      if (steps.length === 1 && command.endsWithFreeText && command.intentType) {
        const opts = buildOptionsForStep(steps[0], undefined, command.meta);
        const tokens = remainder.split(/\s+/).filter((t) => t.length > 0);
        for (let i = tokens.length; i >= 1; i--) {
          const prefix = tokens.slice(0, i).join(' ');
          const match = findUnambiguousOptionMatch(opts, prefix);
          if (!match) continue;
          const body = tokens.slice(i).join(' ').trim();
          if (!body) {
            // Target-only typed (`/sayto bram`) — let the menu flow take
            // over so the user types the body explicitly.
            return null;
          }
          const targetId = match.sourceId ?? '';
          // /givecoins is the one non-communication user of
          // endsWithFreeText. Body is a coin amount ("5 gp"), and the
          // typed intent is `give_coins` at the free action slot — the
          // same finalize the menu path takes around line 2713 in
          // resolveFromSubmit. Without this branch, raw-typed
          // `/givecoins bram 5 gp` fell through to PlayerText (LLM),
          // discarding the picker-resolved target_id.
          let intent: Intent | null = null;
          let actionSlot: 'action' | 'free' = 'action';
          if (command.intentType === 'give_coins') {
            const amountCp = parseCoinAmount(body);
            if (amountCp != null) {
              intent = buildGiveCoinsIntent(amountCp, targetId);
              actionSlot = 'free';
            }
          } else {
            intent = buildCommunicationIntent(command.intentType, body, targetId);
          }
          if (!intent) return null;
          const baseText = buildResult(command, [{ stepType: steps[0], selectedItem: match }]).text;
          return wrapResolved({
            text: `${baseText}${body}`,
            built: { intent, action_slot: actionSlot },
          });
        }
        return null;
      }

      const stepOptions = buildOptionsForStep(steps[0], undefined, command.meta);
      const match = findUnambiguousOptionMatch(stepOptions, remainder);
      if (!match) return null;
      // Legacy single-match finalize for multi-step commands: only safe when
      // ALL remaining steps can be skipped (shouldSkipNextStep / `/use` with
      // auto-self-target on potions). Walk forward through the entire chain
      // to confirm every step from index 1 onward is skippable. Single-skip
      // was wrong with /cast's two consecutive conditional steps —
      // bestow_curse_effect + ability_picker. Pasting `/cast Fireball` would
      // have skipped one and finalized with `targets: []`.
      if (steps.length > 1) {
        if (command.name === 'use') {
          // /use's documented direct-typing finalize policy — skip the
          // full-chain check.
        } else {
          let allSkippable = true;
          for (let i = 1; i < steps.length; i++) {
            if (!shouldSkipNextStep(command, steps[i], match)) {
              allSkippable = false;
              break;
            }
          }
          if (!allSkippable) return null;
        }
      }
      return wrapResolved(buildResult(command, [{ stepType: steps[0], selectedItem: match }]));
    },
    [stage, availableCommands, buildOptionsForStep, buildResult]
  );

  // Submit-time finalizer — see GameSlashCommandState.finalizeForSubmit.
  const finalizeForSubmit = useCallback(
    (text: string): { sendText: string; built: BuiltIntent | null; intent: Intent | null } => {
      const trimmed = text.trim();

      const wrap = (
        sendText: string,
        finalBuilt: BuiltIntent | null
      ): { sendText: string; built: BuiltIntent | null; intent: Intent | null } => ({
        sendText,
        built: finalBuilt,
        intent: finalBuilt?.intent ?? null,
      });

      // Path 1: client-side resolver matched (mid-stage filter or raw /cmd args).
      const resolved = resolveFromSubmit(trimmed);
      if (resolved) {
        return wrap(resolved.text, resolved.built);
      }

      // Path 2: a previous menu/shortcut flow finalised and the user submitted
      // (possibly after typing a free-text suffix beyond the canonical
      // template — e.g. `Say to Goblin: nice day` after the menu produced
      // `Say to Goblin: `). When the canonical matches verbatim, ship the
      // typed Intent the menu built. For endsWithFreeText comm menus
      // (/say, /sayto, /whisper, /shout, /emote), extract the typed body
      // and rebuild the typed communication intent so the intent converter
      // LLM doesn't need to parse the prose. Other deviations drop to
      // `player_text` as before.
      const last = lastBuildResultRef.current;
      if (last) {
        const canonical = last.canonicalText.trim();
        if (trimmed === canonical) {
          return wrap(trimmed, last.built);
        }
        if (last.pendingFreeTextComm && trimmed.startsWith(last.canonicalText)) {
          const body = trimmed.slice(last.canonicalText.length).trim();
          if (body) {
            const { intentType, targetId } = last.pendingFreeTextComm;
            // Communication intents (say/talk_to/whisper/shout/emote) — body
            // is the message; build typed comm intent so the LLM enricher
            // doesn't have to re-parse the prose. The picker-resolved
            // `targetId` IS the recipient — never wasted.
            if (COMMUNICATION_INTENT_TYPES.has(intentType)) {
              const intent = buildCommunicationIntent(intentType, body, targetId ?? undefined);
              if (intent) {
                return wrap(trimmed, { intent, action_slot: 'action' });
              }
            }
            // /givecoins — body is the amount ("5 gp"); parse to copper-pieces
            // and build typed `IntentGiveCoins` with the picked recipient
            // target_id already attached. Run 2 (2026-05-31) confirmed this
            // path was falling through to `player_text` because the rebuild
            // branch only covered comm intents; the picker-selected Lina
            // target_id was discarded and the backend enricher had to
            // re-resolve "Lina" → npc_40d52365. Fixed by generalizing the
            // rebuild branch via FREE_TEXT_REBUILDABLE_INTENT_TYPES.
            if (intentType === 'give_coins' && targetId) {
              const amountCp = parseCoinAmount(body);
              if (amountCp != null) {
                const intent = buildGiveCoinsIntent(amountCp, targetId);
                return wrap(trimmed, { intent, action_slot: 'free' });
              }
            }
          }
        }
        return wrap(trimmed, null);
      }

      // Path 3: pure free typing — no slash, no menu state. Caller routes
      // through `player_text`.
      return wrap(trimmed, null);
    },
    [resolveFromSubmit]
  );

  // Programmatically trigger a spell cast (from Ctrl+N shortcut)
  // Simulates selecting /cast → spell → enters target selection if needed
  const triggerSpellShortcut = useCallback(
    (spellName: string, spellLevel: number, freeCast: boolean = false): SlashSelectResult => {
      // Find the /cast command
      const castCmd = availableCommands.find((c) => c.name === 'cast');
      if (!castCmd) return { action: 'noop' };

      // Build the spell selection as if the user picked it from the spell menu.
      // Tag `concentrationToReplace` when the picked spell is concentration AND
      // the caster is already concentrating on a different spell, so the
      // `concentration_confirm` step actually renders. Without this the Ctrl+N
      // shortcut would silently clobber the existing concentration since
      // `shouldSkipNextStep` skips the confirm whenever the tag is absent.
      //
      // `freeCast` mirrors `buildSpellOptions` / `getSpellCommands`: non-caster
      // innate or Magic-Initiate leveled spells skip the slot picker and emit
      // `free_cast: true`. Without this flag, `slot_level_choice` reads
      // `meta.freeCast` as undefined, treats the cast as slot-paid, finds an
      // empty `max_spell_slots` table on the non-caster, and the wizard resets
      // with "No spell slot to spend."
      const mech = ctx.spellMechanics?.find(
        (m) => m.name.toLowerCase() === spellName.toLowerCase()
      );
      const isConc = (mech as { concentration?: boolean } | undefined)?.concentration === true;
      const currentConc = ctx.playerConcentration ?? null;
      const wouldReplace =
        isConc && currentConc && currentConc.toLowerCase() !== spellName.toLowerCase()
          ? currentConc
          : undefined;
      const spellSelection: StepSelection = {
        stepType: 'spells',
        selectedItem: {
          name: spellName,
          description: '',
          isMenuOption: true,
          sourceId: spellName,
          meta: {
            spellLevel,
            freeCast,
            ...(wouldReplace ? { concentrationToReplace: wouldReplace } : {}),
          },
        },
      };

      saveRecentCommand('cast');
      setRecentNames(getRecentCommands());

      const steps = castCmd.completionSteps ?? [];
      const newSelections = [spellSelection];

      // /cast has steps including conditional bestow_curse_effect +
      // ability_picker after the spells step. Skip step 0 (we know the
      // spell already) and walk past any step that `shouldSkipNextStep`
      // would skip given the seeded spell — otherwise non-Bestow spells
      // (Fireball, etc.) would land on the bestow_curse_effect picker.
      // The slot-choice arm auto-picks for cantrips, free-cast, and
      // single-slot spells so the Ctrl+N shortcut still feels one-tap.
      if (steps.length > 1 && steps[0] === 'spells') {
        let entryIndex = 1;
        while (entryIndex < steps.length) {
          const candidate = steps[entryIndex];
          if (shouldSkipNextStep(castCmd, candidate, spellSelection.selectedItem)) {
            entryIndex += 1;
          } else {
            break;
          }
        }
        if (entryIndex < steps.length) {
          return advanceToStep(castCmd, entryIndex, newSelections);
        }
      }

      // No target step — fill text directly (self-target spell)
      const { text, built: builtIntent } = buildResult(castCmd, newSelections);
      commitFinalResult(text, builtIntent);
      setStage({ type: 'done' });
      return {
        action: 'fill',
        text,
        built: builtIntent,
        intent: builtIntent?.intent ?? null,
        done: true,
      };
    },
    [
      availableCommands,
      advanceToStep,
      buildResult,
      commitFinalResult,
      ctx.spellMechanics,
      ctx.playerConcentration,
    ]
  );

  const triggerWeaponAttackShortcut = useCallback(
    (weaponName: string): SlashSelectResult => {
      const attackCmd = buildWeaponAttackCommand(weaponName);

      saveRecentCommand('attack');
      setRecentNames(getRecentCommands());

      const steps = attackCmd.completionSteps ?? [];
      if (steps.length === 0) {
        const { text, built: builtIntent } = buildResult(attackCmd, []);
        commitFinalResult(text, builtIntent);
        setStage({ type: 'done' });
        return {
          action: 'fill',
          text,
          built: builtIntent,
          intent: builtIntent?.intent ?? null,
          done: true,
        };
      }
      return advanceToStep(attackCmd, 0, []);
    },
    [advanceToStep, buildResult, commitFinalResult]
  );

  const triggerAbilityShortcut = useCallback(
    /**
     * Legacy-path ability shortcut (used when no ActionCard is available).
     *
     * **Note on `targetType`:** the structured-handler block below runs FIRST
     * and overrides `targetType` for any ability in `STRUCTURED_ABILITY_HANDLERS`
     * (Flurry of Blows, Cunning Action, Step of the Wind, Patient Defense,
     * Rage, Innate Sorcery, Dragon Wings, Martial Arts, Metamagic). The
     * caller-supplied `targetType` is honored only for *unstructured* abilities
     * (Second Wind, Action Surge, Lay on Hands, etc.). This is intentional —
     * structured abilities have a fixed shape (toggle / mode / passive /
     * flurry) that ignores any caller hint about target picker mode.
     */
    (
      abilityName: string,
      targetType?: 'self_only' | 'targeted',
      runtime?: { poolRemaining?: number }
    ): SlashSelectResult => {
      // Defensive denylist mirror — fires for label-normalization misses
      // (case mismatch, future denylist additions, /use slash typed in chat),
      // NOT the post-restore null-cards case (Layer 2 fixes that by
      // recomputing `permanent.action_cards` on session load — see
      // `repopulate_action_cards_for_load`). Without these branches,
      // structured abilities would emit bare `UseAbility{name}` and the
      // backend denylist would reject the intent. See
      // `STRUCTURED_ABILITY_HANDLERS` above.
      const handler = lookupStructuredHandler(abilityName);
      if (handler) {
        // Observability: Layer 2 (re-enrich on session load) eliminates the
        // need for this fallback. When this warn stops firing in
        // staging/dev, Layer 2 is working and the mirror becomes dead-path
        // belt-and-suspenders.

        console.warn(
          `[defensive-fallback] Structured ability "${abilityName}" routed without action_card — Layer 2 (re-enrich on session load) needed.`
        );

        if (handler.kind === 'passive') {
          // Should never get a button (PASSIVE_EXCLUSIONS) — defensively noop
          // instead of dispatching a UseAbility that would crash.
          return { action: 'noop' };
        }

        if (handler.kind === 'toggle') {
          // Rage / Innate Sorcery / Dragon Wings — all bonus action per SRD.
          const builtIntent: BuiltIntent = {
            intent: buildToggleAbilityIntent(abilityName),
            action_slot: 'bonus_action',
          };
          const text = `Use ${abilityName}`;
          commitFinalResult(text, builtIntent);
          setStage({ type: 'done' });
          return {
            action: 'fill',
            text,
            built: builtIntent,
            intent: builtIntent?.intent ?? null,
            done: true,
          };
        }

        if (handler.kind === 'mode') {
          // Cunning Action / Step of the Wind / Patient Defense — self-only
          // multi-mode bonus-action abilities. Default to the first mode
          // (matches `buildCardSlashCommand` behavior).
          const builtIntent: BuiltIntent = {
            intent: buildModeAbilityIntent(abilityName, handler.mode_id),
            action_slot: 'bonus_action',
          };
          const text = `Use ${abilityName}`;
          commitFinalResult(text, builtIntent);
          setStage({ type: 'done' });
          return {
            action: 'fill',
            text,
            built: builtIntent,
            intent: builtIntent?.intent ?? null,
            done: true,
          };
        }

        if (handler.kind === 'flurry') {
          // Flurry of Blows — emit Attack{flurry_of_blows:true} and open
          // enemy picker. Mirrors `flurry_card` at builders.rs:378-401.
          // The card-style flurry Intent is built with the picked target on
          // selection; we attach a stub `cardBuilt` envelope so `buildIntent`'s
          // merge layer recognises this as a card-derived flow and doesn't
          // try to build a fresh single-weapon Attack from scratch.
          const flurryStub: IntentAttack = {
            intent_type: 'attack',
            attacks: [],
            flurry_of_blows: true,
          };
          const flurryBuilt: BuiltIntent = {
            intent: flurryStub,
            action_slot: 'bonus_action',
          };
          const cmd: GameSlashCommand = {
            name: `ability-${abilityName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
            description: `Use ${abilityName}`,
            intentType: 'attack',
            category: 'combat',
            roomTypes: ['game'],
            completionSteps: ['enemies'],
            textTemplate: `Attack {target} with ${abilityName}`,
            meta: {
              abilityName,
              cardBuilt: flurryBuilt,
              flurryFinalize: true,
            },
          };
          return advanceToStep(cmd, 0, []);
        }
      }

      // Pool-spend carve-out (#275, 2026-05-10): when a pool ability (Lay on
      // Hands today; future pool abilities follow the same shape) lands here
      // via the click-path fallback — no ActionCard available because
      // post-session-restore enrichment hasn't run yet — we MUST route through
      // the picker instead of the immediate self_only fire. Without this, the
      // legacy `self_only` branch builds `UseAbility{pool_spend: None}` and
      // the engine defaults to 1 HP from the full pool. The caller (typically
      // CharDetailsPanel handleAbilityClick) computes pool remaining from
      // `class_abilities` + `class_resources` directly and threads it via
      // `runtime.poolRemaining`. Detection key: the ability name normalized
      // to lay_on_hands matches today; expand the set if other pool abilities
      // adopt this fallback path.
      const isPoolAbility =
        abilityName.toLowerCase().replace(/[^a-z0-9]+/g, '_') === 'lay_on_hands';
      if (
        isPoolAbility &&
        targetType === 'self_only' &&
        runtime?.poolRemaining !== undefined &&
        runtime.poolRemaining > 0
      ) {
        const cmd: GameSlashCommand = {
          name: `ability-${abilityName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          description: `Use ${abilityName}`,
          intentType: 'use_ability',
          category: 'combat',
          roomTypes: ['game'],
          completionSteps: ['pool_spend'],
          textTemplate: `Use ${abilityName} for {amount} HP`,
          meta: {
            abilityName,
            poolRemaining: runtime.poolRemaining,
            // selfOnlyPool: builds the final UseAbility intent with
            // `pool_spend: <amount>` and target = self. Consumed by
            // build-intent.ts.
            selfOnlyPool: true,
          },
        };
        return advanceToStep(cmd, 0, []);
      }

      // Self-only abilities (Second Wind, Action Surge, Lay on Hands variants
      // without target) — send immediately. Look up the matching ActionCard
      // for canonical slot routing; fall back to 'action' when no card is
      // available (legacy session-restore path before action_cards repopulate).
      // Without the lookup, bonus-action self-buffs (Second Wind, Bardic
      // Inspiration self-targeted, etc.) would land in CharIntent.action and
      // burn the main action slot — Cluster C1 fix, 2026-05-10.
      if (targetType === 'self_only') {
        const matchingCard: ActionCard | undefined = ctx.actionCards?.find(
          (c) => c.activation.kind === 'use_ability' && c.activation.ability_name === abilityName
        );
        const slot: ActionSlot = matchingCard
          ? actionCostToSlot(matchingCard.availability.action_cost)
          : 'action';
        const builtIntent: BuiltIntent = {
          intent: buildSelfUseAbilityIntent(abilityName),
          action_slot: slot,
        };
        const text = `Use ${abilityName}`;
        commitFinalResult(text, builtIntent);
        setStage({ type: 'done' });
        return {
          action: 'fill',
          text,
          built: builtIntent,
          intent: builtIntent?.intent ?? null,
          done: true,
        };
      }

      // Targeted or unknown (default) — show target picker. Curate the roster
      // from the matching ActionCard's `target_picker` when one resolves
      // (enemy → enemies, ally → allies, etc.); fall back to `all_targets`
      // for abilities with no card or no picker (legacy session-restore path,
      // simple abilities like Lay on Hands).
      const targetedCard: ActionCard | undefined = ctx.actionCards?.find(
        (c) => c.activation.kind === 'use_ability' && c.activation.ability_name === abilityName
      );
      const abilityTargetStep: CompletionStepType = targetedCard?.target_picker
        ? targetStepFor(targetedCard.target_picker.kind)
        : 'all_targets';
      const cmd: GameSlashCommand = {
        name: `ability-${abilityName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        description: `Use ${abilityName}`,
        intentType: 'use_ability',
        category: 'combat',
        roomTypes: ['game'],
        completionSteps: [abilityTargetStep],
        textTemplate: `Use ${abilityName} on {target}`,
        meta: { abilityName },
      };
      return advanceToStep(cmd, 0, []);
    },
    [advanceToStep, commitFinalResult, ctx.actionCards]
  );

  const triggerCardShortcut = useCallback(
    (
      card: import('@danda/shared').ActionCard,
      runtime?: { poolRemaining?: number }
    ): SlashSelectResult => {
      // Shared with slash-command autocomplete — see build-card-command.ts.
      // Click and autocomplete now emit identical commands, so `cardIntent`
      // and the correct `intentType` are always present.
      const cmd = buildCardSlashCommand(card, { poolRemaining: runtime?.poolRemaining });
      if (!cmd) return { action: 'noop' };

      saveRecentCommand(cmd.name);
      setRecentNames(getRecentCommands());

      // Zero-step: fill text directly and resolve the typed Intent via
      // buildResult so the same merge logic (including `meta.cardIntent`)
      // runs whether the command reached us through click or autocomplete.
      if (!cmd.completionSteps || cmd.completionSteps.length === 0) {
        const { text, built: builtIntent } = buildResult(cmd, []);
        commitFinalResult(text, builtIntent);
        setStage({ type: 'done' });
        return {
          action: 'fill',
          text,
          built: builtIntent,
          intent: builtIntent?.intent ?? null,
          done: !cmd.endsWithFreeText,
        };
      }
      return advanceToStep(cmd, 0, []);
    },
    [advanceToStep, buildResult, commitFinalResult]
  );

  const triggerItemShortcut = useCallback(
    (data: ItemTriggerData): SlashSelectResult => {
      const { itemName, itemId, effectType, spells, abilities, charges } = data;

      // Build synthetic command based on effect type
      if (effectType === 'cast_spell_charges' && spells?.length) {
        if (spells.length === 1) {
          // Single spell — skip spell picker, go straight to target
          const spell = spells[0];
          const cmd: GameSlashCommand = {
            name: `item-${itemName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
            description: `Use ${itemName}`,
            intentType: 'use_item',
            category: 'combat',
            roomTypes: ['game'],
            completionSteps: ['all_targets'],
            textTemplate: `Use ${itemName} to cast ${spell.spellName} at {target}`,
            meta: {
              itemName,
              itemId,
              spellName: spell.spellName,
              spellCharges: spell.charges ?? 1,
              // Stamp the spell's audience so the all_targets chokepoint can
              // curate the target roster (e.g., Wand of Fireball → enemies).
              // `target_audience` is stamped by the enricher when the item spell
              // is in the character's spell list; falls back to full roster otherwise.
              ...(spell.target_audience ? { spellTargetAudience: spell.target_audience } : {}),
            },
          };
          return advanceToStep(cmd, 0, []);
        }
        // Multi-spell — spell picker then target
        const cmd: GameSlashCommand = {
          name: `item-${itemName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          description: `Use ${itemName}`,
          intentType: 'use_item',
          category: 'combat',
          roomTypes: ['game'],
          completionSteps: ['item_spells', 'all_targets'],
          textTemplate: `Use ${itemName} to cast {spell} at {target}`,
          meta: { itemName, itemId, itemSpells: spells, itemCharges: charges ?? 0 },
        };
        return advanceToStep(cmd, 0, []);
      }

      if (effectType === 'special_abilities' && abilities?.length) {
        const onUse = abilities.filter((a) => a.trigger === 'on_use');
        if (onUse.length === 1) {
          // Single on_use ability — skip picker, go straight to target.
          // Best-effort target curation: route through the item ActionCard's
          // TargetPicker when one is resolvable. `cards_from_items` is a stub
          // today (no item cards emitted), so this almost always misses and
          // falls back to `all_targets` — wired so it lights up for free once
          // item cards exist, with no regression in the meantime.
          const ability = onUse[0];
          const itemCard: ActionCard | undefined = ctx.actionCards?.find(
            (c) => c.activation.kind === 'use_ability' && c.activation.ability_name === ability.name
          );
          const itemAbilityStep: CompletionStepType = itemCard?.target_picker
            ? targetStepFor(itemCard.target_picker.kind)
            : 'all_targets';
          const cmd: GameSlashCommand = {
            name: `item-${itemName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
            description: `Use ${itemName}`,
            intentType: 'use_item',
            category: 'combat',
            roomTypes: ['game'],
            completionSteps: [itemAbilityStep],
            textTemplate: `Use ${itemName}'s ${ability.name} on {target}`,
            meta: {
              itemName,
              itemId,
              abilityName: ability.name,
              abilityCharges: ability.charges ?? 1,
            },
          };
          return advanceToStep(cmd, 0, []);
        }
        // Multi-ability — ability picker then target
        const cmd: GameSlashCommand = {
          name: `item-${itemName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          description: `Use ${itemName}`,
          intentType: 'use_item',
          category: 'combat',
          roomTypes: ['game'],
          completionSteps: ['item_abilities', 'all_targets'],
          textTemplate: `Use ${itemName}'s {ability} on {target}`,
          meta: { itemName, itemId, itemAbilities: onUse },
        };
        return advanceToStep(cmd, 0, []);
      }

      if (effectType === 'cast_spell' || effectType === 'targeted') {
        // cast_spell (fixed spell, no charges) or simple targeted item — target picker
        const cmd: GameSlashCommand = {
          name: `item-${itemName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          description: `Use ${itemName}`,
          intentType: 'use_item',
          category: 'combat',
          roomTypes: ['game'],
          completionSteps: ['all_targets'],
          textTemplate: `Use ${itemName} on {target}`,
          meta: { itemName, itemId },
        };
        return advanceToStep(cmd, 0, []);
      }

      // Self-only items (detection, summon_creature, etc.) — send immediately.
      // Look up the matching ActionCard for canonical slot routing; fall back
      // to 'action' when no card is available.
      //
      // **Future-proofing note:** the backend's `cards_from_items` builder
      // (`backend/shared/src/enricher/mechanics/action_cards/builders.rs`) is
      // a stub returning `Vec::new()` today, so `ctx.actionCards` never
      // contains item cards in production — every call falls through to
      // `'action'`. Once item cards are wired, bonus-action magic items
      // will route correctly without further FE changes.
      const matchingItemCard: ActionCard | undefined = ctx.actionCards?.find(
        (c) => c.activation.kind === 'use_item' && c.activation.item_id === itemId
      );
      const itemSlot: ActionSlot = matchingItemCard
        ? actionCostToSlot(matchingItemCard.availability.action_cost)
        : 'action';
      const builtIntent: BuiltIntent = {
        intent: buildSelfUseItemIntent(itemName, itemId),
        action_slot: itemSlot,
      };
      const text = `Use ${itemName}`;
      commitFinalResult(text, builtIntent);
      setStage({ type: 'done' });
      return {
        action: 'fill',
        text,
        built: builtIntent,
        intent: builtIntent?.intent ?? null,
        done: true,
      };
    },
    [advanceToStep, commitFinalResult, ctx.actionCards]
  );

  return {
    menuItems,
    showMenu,
    selectedIndex,
    stageLabel,
    isMultiStep,
    isActive,
    handleTextChange,
    handleSelect,
    handleBack,
    setSelectedIndex,
    built,
    intent: built?.intent ?? null,
    reset,
    resolveFromSubmit,
    finalizeForSubmit,
    triggerSpellShortcut,
    triggerWeaponAttackShortcut,
    triggerAbilityShortcut,
    triggerItemShortcut,
    triggerCardShortcut,
  };
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Whether the picker should skip the upcoming step after `selectedItem` was
 * picked. Used by `/use` to chain into `friendly_targets` ONLY when the
 * picked item is a healing/buff consumable — scrolls, tools, and other
 * usable items that target enemies / scene objects / nothing finalize
 * after the item pick instead of forcing an inappropriate target picker.
 *
 * Keep the rules tight (currently `friendly_targets` after a `'potion'`
 * item kind). Future categories with friendly-target semantics (e.g.
 * Healer's Kit when it lands as `'consumable'`, friendly-buff scrolls)
 * can be added here once their effect data is reliably surfaced.
 */
function shouldSkipNextStep(
  cmd: GameSlashCommand,
  nextStep: CompletionStepType | undefined,
  selectedItem: SlashCommandDefinition
): boolean {
  if (!nextStep) return false;
  if (cmd.name === 'use' && nextStep === 'friendly_targets') {
    const itemKind = selectedItem.meta?.itemKind as string | undefined;
    return itemKind !== 'potion';
  }
  // /cast's `bestow_curse_effect` step is only relevant when the selected
  // spell IS Bestow Curse. For every other spell, skip the picker so the
  // flow proceeds straight to slot_level_choice + all_targets.
  if (cmd.name === 'cast' && nextStep === 'bestow_curse_effect') {
    return selectedItem.name.toLowerCase() !== 'bestow curse';
  }
  // /cast's `ability_picker` is the Bestow Curse AbilityDisadvantage sub-
  // pick. Skip unless the previous selection was AbilityDisadvantage.
  // `selectedItem` here is the item picked in the previous step (the
  // bestow_curse_effect choice); check its meta.bestowCurseVariant.
  if (cmd.name === 'cast' && nextStep === 'ability_picker') {
    const variant = selectedItem.meta?.bestowCurseVariant as string | undefined;
    return variant !== 'AbilityDisadvantage';
  }
  // `concentration_confirm` runs only when the picked spell would replace
  // an active concentration. `buildSpellOptions` tags such spells with
  // `meta.concentrationToReplace = <currentConc name>`; skip when the tag
  // is absent. Universal (not `cmd.name === 'cast'`) so action-card
  // metamagic flows (Careful/Twinned/Transmuted/Quickened/etc.) that
  // include `concentration_confirm` after their `spells` step also gate
  // on the swap tag. Per-spell slash commands put `concentration_confirm`
  // at step 0 and never call this predicate for it.
  if (nextStep === 'concentration_confirm') {
    return selectedItem.meta?.concentrationToReplace == null;
  }
  return false;
}

/** Get the base text to show in input when entering a step */
function getBaseTextForStep(cmd: GameSlashCommand, _stepIndex: number): string {
  // For the first step, show the beginning of the template
  if (cmd.textTemplate) {
    // e.g., "Cast {spell}" → "Cast "
    const firstPlaceholder = cmd.textTemplate.match(/\{[^}]+\}/);
    if (firstPlaceholder) {
      return cmd.textTemplate.slice(0, firstPlaceholder.index);
    }
  }
  return cmd.fillText ?? '';
}

function buildWeaponAttackCommand(weaponName: string): GameSlashCommand {
  return {
    name: `attack-${weaponName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    description: `Attack with ${weaponName}`,
    intentType: 'attack',
    category: 'combat',
    roomTypes: ['game'],
    completionSteps: ['enemies'],
    textTemplate: `Attack with my ${weaponName} at {target}`,
    meta: { weaponName },
  };
}

/** Build partial text from selections so far */
function buildPartialText(cmd: GameSlashCommand, selections: StepSelection[]): string {
  if (!cmd.textTemplate) return cmd.fillText ?? '';

  let text = cmd.textTemplate;
  for (const sel of selections) {
    const name = sel.selectedItem.name;
    switch (sel.stepType) {
      case 'spells':
        text = substitutePlaceholder(text, '{spell}', name);
        break;
      case 'enemies':
      case 'allies':
      case 'allies_or_self':
      case 'dying_allies':
      case 'dead_allies':
      case 'all_targets':
      case 'friendly_targets':
      case 'scene_chars':
      case 'scene_npcs':
      case 'party_members':
        text = substitutePlaceholder(text, '{target}', name);
        break;
      case 'inventory_items':
      case 'usable_items':
      case 'equippable_items':
      case 'scene_items':
        text = substitutePlaceholder(text, '{item}', name);
        break;
      case 'scene_objects':
      case 'openable_objects':
      case 'closable_objects':
      case 'breakable_objects':
        text = substitutePlaceholder(text, '{object}', name);
        break;
      case 'item_spells':
        text = substitutePlaceholder(text, '{spell}', name);
        break;
      case 'item_abilities':
        text = substitutePlaceholder(text, '{ability}', name);
        break;
      case 'pool_spend': {
        const amount = (sel.selectedItem.meta?.poolSpend as number) ?? 1;
        text = text.replace('{amount}', `${amount}`);
        break;
      }
      case 'travel_destinations':
        text = substitutePlaceholder(text, '{destination}', name);
        break;
      case 'transmuted_damage_type': {
        const metaType = sel.selectedItem.meta?.transmutedDamageType as string | undefined;
        const label = metaType ?? name.replace(/^[^\sA-Za-z]+\s*/, '');
        const display = label.charAt(0).toUpperCase() + label.slice(1);
        text = text.replace('{damageType}', display);
        break;
      }
      case 'slot_level_choice':
      case 'twinned_target':
      case 'careful_exclude':
      case 'concentration_confirm':
        // None of these fill template placeholders — canonical text
        // stays "Cast {spell} at {target}". Picks live on the Intent
        // (or drive side-effects, in the concentration-confirm case).
        break;
    }
  }

  // Find the next unfilled placeholder and trim there
  const nextPlaceholder = text.match(/\{[^}]+\}/);
  if (nextPlaceholder && nextPlaceholder.index !== undefined) {
    // Drop trailing connector words ("on", "at", "to", "with") that the
    // template put before the unfilled placeholder — e.g.
    // `Use Lantern on {target}` → `Use Lantern on ` would read as a dangling
    // sentence. Strip the connector + surrounding whitespace so the final
    // text is `Use Lantern`.
    return text
      .slice(0, nextPlaceholder.index)
      .replace(/\s+(on|at|to|with)\s*$/i, '')
      .trimEnd();
  }

  return text;
}
